/*
 * The intent to this file is to hold models that need to be shared between noodle-api and clients (noodle-frontent).
 * A copy of this file should exist on every client that interacts with noodle-api.
 *
 * There should not be any associations in these definitions because it is hard to guarantee that all of the properties exist on the associated model.
 * The consumer of this file should do something like Pick<ThisType, 'id'> & { associatedModel: Pick<AssociatedModel, 'id'> }
 * NOT Pick<ThisType, 'id' | 'associatedModel'> because this would say that the whole AssociatedModel is attached.
 *
 * Any scalars that need to be different between the client and the server should be taken care of by the respective Scalars files.
 *
 * DateTime
 *   on noodle-api datetime fields are Date objects.
 *   on clients, these will be strings.
 *
 */
import { type Descendant } from 'slate';
import { z } from 'zod';

import type * as DocumentApiModels from '@/tsClient/servers/noodleDocuments/document-api-models';
import { Roles, type SlateElement, type SlateNode } from '@/typings/common';
import * as GraphQLModels from '@/typings/graphql-models';
import type StripeModels from '@/typings/Stripe';
import { type NoOptionalProperties } from '@/typings/utility';

export type Scalars = {
  ID: string;
  String: string;
  Boolean: boolean;
  Int: number;
  Float: number;
  Date: string;
  DateTime: string;
  Json: Record<string, unknown>;
  Hex: string;
};

export type RichText = {
  html: string;
  text: string;
};

export type Asset = {
  fileName: string;
  id: string;
  mimeType?: string | null;
  url: string;
  size?: number | null;
};

export type Color = {
  hex: Scalars['Hex'];
};

export const { Currency } = GraphQLModels;
export type Currency = GraphQLModels.Currency;

export enum CurrencyUnknown {
  Unknown = 'Unknown',
}

export type CurrencyType = 'USD' | 'CAD' | 'EUR' | 'GBP';
export type CurrencyValue = {
  currency: CurrencyType;
  amount: number | null | CurrencyUnknown;
  text: string | null;
  isUnknown?: boolean;
};

export type IntlAddress = {
  city: string;
  country: string;
  state?: string;
  line1: string;
  line2?: string;
  postCode: string;
};

export type USAddress = {
  city: string;
  state: string;
  line1: string;
  line2?: string;
  zip: string;
};

export type Name = {
  firstName: string;
  middleName?: string;
  lastName: string;
};

export interface DebtorData {
  // Check for joint filing
  filingJointly?: 'No' | 'Yes';

  // Statistics Record Fields (1-42)
  formOfOrganization?: undefined;
  natureOfBusiness?: undefined;
  chapter?: '7' | '9' | '11' | '12' | '13' | '15';
  feeStatus?: 'i' | 'p' | 'w';
  natureOfDebt?: 'b' | 'c' | 'o';
  assetNotice?: 'y' | 'n' | 'u';
  numberOfCreditors?: number | string;
  estimatedAssets?: number | string;
  estimatedLiabilities?: number | string;
  smallBusiness?: undefined;
  certification?: 'y' | 'w' | 'u';
  presumption?: 'y' | 'n';
  priorFiling?: 'y' | 'n';

  // Financial amounts
  realEstate?: number | string;
  personalProperty?: number | string;
  securedClaims?: number | string;
  priorityUnsecured?: number | string;
  nonpriorityUnsecured?: number | string;
  monthlyIncome?: number | string;
  monthlyExpenses?: number | string;
  currentMonthlyIncome?: number | string;
  totalNondischargeable?: number | string;
  totalExemptions?: number | string;
  monthlyGrossWagesDebtor?: number | string;
  monthlyGrossWagesSpouse?: number | string;
  monthlyPayrollDebtor?: number | string;
  monthlyPayrollSpouse?: number | string;
  monthlyNetIncome?: number | string;

  // Case Upload Fields (43-80)
  maritalFilingStatus?: 'a' | 'b' | 'c' | 'd' | '';
  totalCurrentMonthlyIncomeDebtor?: number | string;
  totalCurrentMonthlyIncomeSpouse?: number | string;
  applicableMedianFamilyIncome?: number | string;
  debtorHouseholdSize?: number | string;
  adjustedCurrentMonthlyIncome?: number | string;
  nationalStandardsFood?: number | string;
  nationalStandardsHealthUnder65?: number | string;
  nationalStandardsHealth65Plus?: number | string;
  localStandardsHousingNonMortgage?: number | string;
  localStandardsHousingMortgage?: number | string;
  localStandardsTransportation?: number | string;
  additionalPublicTransportation?: number | string;
  numberOfVehicles?: '0' | '1' | '2' | '';
  vehicle1OwnershipExpense?: number | string;
  vehicle2OwnershipExpense?: number | string;
  irsExpenseAllowances?: number | string;
  educationExpenses?: number | string;
  additionalFoodClothing?: number | string;
  totalAdditionalExpenses?: number | string;
  totalDebtPaymentDeductions?: number | string;
  totalAllDeductions?: number | string;
  monthlyDisposableIncome?: number | string;
  sixtyMonthDisposableIncome?: number | string;
  initialPresumptionDetermination?: 'n' | 'y' | 'u' | '';
  totalNonpriorityUnsecuredDebt?: number | string;
  twentyFivePercentUnsecured?: number | string;
  secondaryPresumptionDetermination?: 'n' | 'y' | 'u' | '';
  relatedCaseNumber?: undefined;
  relatedCaseOfficeCode?: undefined;
  investmentCompany?: undefined;
  investmentAdvisor?: undefined;
  naicsCode?: undefined;
  periodicReports?: undefined;
  shellCompany?: undefined;
  reliefInPlan?: undefined;
  caseSubchapter?: 'V' | '';

  // Debtor Record Fields (1-21)
  partyRole?: undefined;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  title?: string;
  generation?: undefined;
  ssn?: string;
  taxId?: string;
  officeCode?: string;
  street1?: string;
  street2?: string;
  street3?: undefined;
  city?: string;
  state?: string;
  zip?: string;
  county?: string;
  country?: undefined;
  phoneNumber?: string;
  partyText?: undefined;
  email?: string;
  fax?: undefined;
  creditCounselingCompletionDate?: string;

  // Debtor 2
  d2partyRole?: undefined;
  d2firstName?: string;
  d2middleName?: string;
  d2lastName?: string;
  d2title?: string;
  d2generation?: undefined;
  d2ssn?: string;
  d2taxId?: string;
  d2officeCode?: string;
  d2street1?: string;
  d2street2?: string;
  d2street3?: undefined;
  d2city?: string;
  d2state?: string;
  d2zip?: string;
  d2county?: string;
  d2country?: undefined;
  d2phoneNumber?: string;
  d2partyText?: undefined;
  d2email?: string;
  d2fax?: undefined;
  d2creditCounselingCompletionDate?: string;

  // Alias Record Fields (1-8)
  aliases?: {
    aliasPartyRole?: 'db';
    aliasType?: 'aka' | 'fka' | 'dba';
    aliasFirstName?: string;
    aliasMiddleName?: string;
    aliasLastName?: string;
    aliasTitle?: string;
    aliasGeneration?: string;
  }[];

  d2aliases?: {
    aliasPartyRole?: 'jdb';
    aliasType?: 'aka' | 'fka' | 'dba';
    aliasFirstName?: string;
    aliasMiddleName?: string;
    aliasLastName?: string;
    aliasTitle?: string;
    aliasGeneration?: string;
  }[];

  nonConsumerDebts?: 'y' | 'n';
  veteransDeclaration?: 'y' | 'n';

  attorneyName?: string;

  isDisposableIncomeDetermined?: 'y' | 'n';
  commitmentPeriod?: '3y' | '5y';

  // Additional fields for flexibility
  [key: string]: unknown;
}

export type AvailabilityBlock = {
  id: Scalars['ID'];
  startAt: Scalars['DateTime'];
  endAt: Scalars['DateTime'];
  creatorTimezone: string;
  status: string;
  creatorId: string;
  productId: string;
};

export type AvailabilityGroup = {
  id: Scalars['ID'];
  startAt: Scalars['DateTime'];
  endAt: Scalars['DateTime'];
  numDays: number;
  days: number[];
  creatorId: string;
  productId: string;
};

export enum StripeAccountType {
  Connect = 'Connect',
  Express = 'Express',
  NoodleMor = 'NoodleMOR',
}

export enum CustomTermsDynamicVariableType {
  Info = 'info',
  Signature = 'signature',
  SignatureDate = 'signatureDate',
}

export enum AddressUnitType {
  Apartment = 'APT',
  Floor = 'Floor',
  Suite = 'Suite',
}

export enum BureauType {
  Equifax = 'EQUIFAX',
  TransUnion = 'TRANSUNION',
  Experian = 'EXPERIAN',
  B3 = '3b',
  B1 = '1b',
  B2B = 'B2B',
}

export enum BureauService {
  Bankruptcy = 'bankruptcy',
  BankruptcyVantage4 = 'bankruptcy-vantage4',
  PrequalVantage4 = 'prequal-vantage4',
}

export type Account = {
  createdAt: Scalars['DateTime'];
  creatorId: string;
  id: string;
  provider: 'crs-b2b' | 'plaid' | 'court-api' | 'quickbooks';
  username: string;
};

export type TeamRole = 'admin' | 'finance' | 'caseWorker';

export type TeamMemberRoles = {
  id: string;
  teamMemberId: string;
  admin: boolean;
  finance: boolean;
  manageTeam: boolean;
  role: TeamRole;
};

export type Creator = NoOptionalProperties<
  Pick<
    GraphQLModels.Creator,
    | 'businessCategory'
    | 'confidoFirmId'
    | 'confidoStatus'
    | 'contactPhoneNumber'
    | 'countryCode'
    | 'creatorDomain'
    | 'creatorSupportEmail'
    | 'defaultCurrency'
    | 'facebookFollowers'
    | 'facebookUrl'
    | 'followupCadence'
    | 'followupCadencePercentage'
    | 'followupCadenceUnits'
    | 'hasFinishedOnboarding'
    | 'id'
    | 'instagramFollowers'
    | 'instagramUrl'
    | 'isAiEnabled'
    | 'isBuyEnabled'
    | 'name'
    | 'noodlePhoneNumber'
    | 'notifyOnEveryCustomerMessage'
    | 'oneLiner'
    | 'personId'
    | 'routableCompanyId'
    | 'slug'
    | 'stripeAccountType'
    | 'stripe_account_id'
    | 'successPaymentRedirectUrl'
    | 'tikTokUrl'
    | 'tiktokFollowers'
    | 'timezone'
    | 'twitterFollowers'
    | 'twitterUrl'
    | 'utmCampaignId'
    | 'utmContent'
    | 'utmSource'
    | 'webUrl'
    | 'youtubeFollowers'
    | 'youtubeUrl'
    | 'isB2BEnabled'
    | 'bio'
  >
> & {
  alwaysShowProfileChat: boolean;
  defaultCurrency?: Currency | null;
  hasSetUpAgency: boolean;
  isAttorney: boolean;
  lastSeen?: Scalars['DateTime'];
  logoForBranding?: Pick<Asset, 'url'> | null;
  logoCompact?: Pick<Asset, 'url'> | null;
  needsPaymentMethod: boolean;
  nextBillingDate: string | null;
  numLeads: number;
  numRecentBroadcasts: number;
  paymentProvider: 'stripe' | 'confido' | 'none';
  usesConfido: boolean;
  usesStripe: boolean;
};

export type ConfidoStatus = 'CREATED' | 'APP_IN_DRAFT' | 'APP_SUBMITTED' | 'APP_IN_REVIEW' | 'ACTIVE' | 'INACTIVE';
export type ConfidoPaymentMethod = 'CREDIT' | 'DEBIT' | 'ACH';

export type CreatorSocialMedia = {
  githubAvatarUrl: Scalars['String'] | null;
  githubEmail: Scalars['String'] | null;
  githubId: Scalars['String'] | null;
  githubName: Scalars['String'] | null;
  githubUsername: Scalars['String'] | null;
  id: Scalars['ID'];
  instagramBio: Scalars['String'] | null;
  instagramFollowerCount: Scalars['Int'] | null;
  instagramFullName: Scalars['String'] | null;
  instagramId: Scalars['String'] | null;
  instagramProfilePhotoUrl: Scalars['String'] | null;
  instagramUsername: Scalars['String'] | null;
  linkedinId: Scalars['String'] | null;
  linkedinName: Scalars['String'] | null;
  tiktokFollowerCount: Scalars['Int'] | null;
  tiktokUsername: Scalars['String'] | null;
  twitterFollowersCount: Scalars['Int'] | null;
  twitterId: Scalars['String'] | null;
  twitterName: Scalars['String'] | null;
  twitterProfileImageUrl: Scalars['String'] | null;
  twitterUsername: Scalars['String'] | null;
  youtubeChannelDescription: Scalars['String'] | null;
  youtubeChannelId: Scalars['String'] | null;
  youtubeChannelName: Scalars['String'] | null;
  youtubeChannelSubscriberCount: Scalars['Int'] | null;
  youtubeChannelVideoCount: Scalars['Int'] | null;
  youtubeChannelViewCount: Scalars['Int'] | null;
};

export type PaymentRefund = {
  id: string;
  amount: number;
  createdAt: string;
  status: 'pending' | 'succeeded' | 'failed' | 'unknown';
  type: 'refund' | 'void' | 'dispute';
};

export type Payment = {
  amount: number;
  amountRefunded: number;
  applicationFees: number;
  /** @deprecated Use providerId instead */
  confidoTransactionId: string | null;
  createdAt: Scalars['DateTime'];
  creatorCurrency: string | null;
  customerCurrency: string | null;
  discounts: number;
  failureReason: string | null;
  id: Scalars['ID'];
  invoiceId: string | null;
  last4: string | null;
  net: number;
  netRevenue: number;
  notes: string | null;
  paymentPlanId: string | null;
  passedProcessingFeesToCustomer: boolean;
  paymentMethodType: string | null;
  // paymentMethodType: 'outside-of-noodle' | 'Card' | 'ACH' | null;
  paymentStatus: 'succeeded' | 'requires_payment_method' | 'failed' | 'pending' | 'voided';
  payoutId: string | null;
  refunds: Array<Pick<PaymentRefund, 'id' | 'amount' | 'createdAt' | 'status' | 'type'>>;
  personId: string | null;
  personProductId: string | null;
  personSubscriptionId: string | null;
  priceId: string | null;
  priceTitle: string | null;
  processingFees: number;
  processingFeesRefunded: number;
  productId: string | null;
  productTitle: string | null;
  provider: 'stripe' | 'confido' | 'outside-of-noodle';
  providerId: string | null;
  succeededAt: Scalars['DateTime'] | null;
  taxAmount: number;
  serviceAmount: number;
  type: string;
};

export type Payout = {
  id: Scalars['ID'];
  amount: number;
  endAt: Scalars['DateTime'] | null;
  submittedAt: Scalars['DateTime'] | null;
  expectedPaidDate: Scalars['DateTime'] | null;
  status: string;
  routablePayoutId: string | null;
  creatorId: string | null;
  paymentsCount: number;
  createdAt: Scalars['DateTime'];
};

export type Person = {
  id: string;
  email: string | null;
  role: Roles;
  phoneNumber: string | null;
  contactPhoneNumber: string | null;
  name: string | null;
  isVerified: boolean;
  backupAuthData: string | null;
  createdAt: Scalars['DateTime'];
  preferredLanguage: string | null;
  primaryColour: Scalars['Hex'] | null;
};

export type PersonAttorney = {
  id: string;
  email: string;
  personId: string;
  barNumber: string | null;
  firstName: string;
  middleName: string | null;
  lastName: string;
  daytimePhoneNumber: string | null;
  mobilePhoneNumber: string | null;
  organizationName: string | null;
  uscisAccountNumber: string | null;
  faxNumber: string | null;
  licensingAuthority: string | null;
  mailingAddressCity: string | null;
  mailingAddressState: string | null;
  mailingAddressStreet: string | null;
  mailingAddressUnit: AddressUnitType | null;
  mailingAddressUnitNumber: number | null;
  mailingAddressZip: string | null;
  physicalAddressCity: string | null;
  physicalAddressState: string | null;
  physicalAddressStreet: string | null;
  physicalAddressUnit: AddressUnitType | null;
  physicalAddressUnitNumber: number | null;
  physicalAddressZip: string | null;
  creatorId: string | null;
  isDefaultFilingAttorney: boolean;
};

export enum NoodleProductTypes {
  Broadcast = 'Broadcast',
  Byte = 'Byte',
  Charge = 'Charge',
  Handbook = 'Handbook',
  InPersonSession = 'InPersonSession',
  Lite = 'Lite',
  Noodle = 'Noodle',
  NoodleSaaS = 'NoodleSaaS',
  OnlineSession = 'OnlineSession',
  ProductGroup = 'ProductGroup',
  Response = 'Response',
  Subscription = 'Subscription',
  Consultation = 'Consultation',
  // This was added to noodle-frontend, but isn't a real type. Is only there for the CreateServiceForm
  // Need a better way to handle
  Collection = 'Collection',
}

export enum SubscriptionStatus {
  Active = 'Active',
  Canceled = 'Canceled',
  Expired = 'Expired',
  Incomplete = 'Incomplete',
  IncompleteExpired = 'Incomplete_expired',
  PastDue = 'Past_due',
  Trialing = 'Trialing',
  Unpaid = 'Unpaid',
}

export enum ProductState {
  Free = 'free',
  NotForSale = 'not_for_sale',
  OnSale = 'on_sale',
}

export enum CaptureMethod {
  AUTOMATIC = 'automatic',
  AUTO_ASYNC = 'automatic_async',
  MANUAL = 'manual',
}

export type NoodleProductPrice = {
  id: string;
  price: number;
  priceTitle?: string | null;
  priceSubDescription?: string | null;
  isActive?: boolean | null;
  numBookings?: number | null;
  currency: GraphQLModels.Currency;
  priceDescription?: { html: string } | null;
};

export type Product = Pick<
  GraphQLModels.Product,
  | 'address'
  | 'allowVideoConferenceLink'
  | 'allowBookingRescheduleWithin48hrsTimeLimit'
  | 'canUpsell'
  | 'confidoBankAccountId'
  | 'createdAt'
  | 'description'
  | 'id'
  | 'includeHandbooks'
  | 'includesBroadcasts'
  | 'includesFreeMembership'
  | 'isActive'
  | 'isTierBasedPermissioningEnabled'
  | 'lastUpdate'
  | 'noodleApplicationFlatFee'
  | 'secondaryStripeAccountId'
  | 'shortDescription'
  | 'showOnOneLink'
  | 'includeAsIntakeConsultationMetrics'
  | 'slug'
  | 'state'
  | 'stripeId'
  | 'title'
  | 'initiateCaseRank'
  | 'numConcurrentBookings'
  | 'calendarEventNote'
  | 'order'
> & {
  defaultSchedulingPersonId: string | null;
  isBuyEnabled: boolean;
  showBookingLinkInUserProfileMeetings?: boolean | null;
};

export type ProductType = {
  id: string;
  noodleProductType?: NoodleProductTypes | null;
  slug: string;
  title?: string | null;
  thumbnail?: Pick<Asset, 'url'> | null;
};

export type Course = {
  id: string;
  title?: string | null;
  description?: RichText | null;
  slug: string;
  productTypes: Array<Pick<ProductType, 'id'>>;
};

export type HandbookStep = {
  id: string;
  repetitionAmount?: number | null;
  step?: number | null;
};

export type HandbookPhoto = Pick<
  GraphQLModels.Asset,
  'id' | 'url' | 'mimeType' | 'youTubeLink' | 'vimeoUri' | 'fileName' | 'size'
> & {
  loomEmbedUrl?: string | null;
};

export enum TaxBehavior {
  Exclusive = 'exclusive',
  Inclusive = 'inclusive',
  Unspecified = 'unspecified',
}

export enum Interval {
  Day = 'DAY',
  Month = 'MONTH',
  Sixmonths = 'SIXMONTHS',
  Threemonths = 'THREEMONTHS',
  Week = 'WEEK',
  Year = 'YEAR',
}

export enum StripePriceUsageType {
  OneTime = 'ONE_TIME',
  Recurring = 'RECURRING',
}

export type Price = Pick<
  GraphQLModels.Price,
  | 'bookingCalendarLink'
  | 'currency'
  | 'id'
  | 'isActive'
  | 'isArchived'
  | 'isMostPopular'
  | 'numBookings'
  | 'price'
  | 'priceSubDescription'
  | 'priceTitle'
  | 'recurringInterval'
  | 'sessionBuffer'
  | 'sessionDuration'
  | 'sessionInterval'
  | 'sessionTime'
  | 'stripePriceId'
  | 'trialPeriodDays'
  | 'usageType'
  | 'isUpsell'
  | 'tierId'
  | 'isAchPaymentEnabled'
  | 'isCardPaymentEnabled'
  | 'isDebitCardPaymentEnabled'
  | 'originalPrice'
  | 'secondaryStripeAccountId'
  | 'confidoBankAccountId'
> & {
  createdAt: Scalars['DateTime'];
  isExemptFromApplicationFee: boolean;
  passProcessingFeesToCustomer: boolean;
};

export type Statistics = {
  invoices: number;
  invoiceFollowups: number;
  customTerms: number;
  customTermsFollowups: number;
  formRequests: number;
  formRequestFollowups: number;
  documentRequestFollowups: number;
  aiResponses: number;
  anvilDocs: number;
  uploadedDocs: number;
  activeMemberships: number;
  commentsByMembers: number;
  messagesSent: number;
  conversationsCreated: number;
};

export type RoutableCompany = {
  id: string;
  type: string;
  contacts: {
    results: Array<{
      id: string;
    }>;
  };
  created_at: Scalars['DateTime'];
  display_name: string;
  external_id: string | null;
  is_customer: boolean;
  is_vendor: boolean;
  ledger: {
    customer_id: string | null;
    vendor_id: string | null;
  };
  status: string;
  business_name: string;
};

export type ScheduledEvent = {
  id: Scalars['ID'];
  eventName: string;
  referenceId: string;
  referenceType: string;
  data: Scalars['Json'] | null;
  executeAt: Scalars['DateTime'];
  isExecuted: boolean;
};

export type Booking = {
  id: Scalars['ID'];
  startAt: Scalars['DateTime'] | null;
  endAt: Scalars['DateTime'] | null;
  productId: string;
  priceId: string;
  skippedAt: Scalars['DateTime'] | null;
  canceledAt: Scalars['DateTime'] | null;
  teamMemberPersonId?: string | null;
  meetingLink: string | null;
  userWorkflowId: string | null;
  createdAt: Scalars['DateTime'];
  updatedAt: Scalars['DateTime'];
};

export type BookingPermission = {
  id: string;
  bookingId: string;
  personId: string;
};

export enum MessageType {
  ByteRoot = 'ByteRoot',
  ByteStep = 'ByteStep',
  ProductDiscussion = 'ProductDiscussion',
  PaidBroadcast = 'PaidBroadcast',
  FreeBroadcast = 'FreeBroadcast',
  Comment = 'Comment',
  Chat = 'Chat',
  ChatRoot = 'ChatRoot',
  WorkflowDiscussionRoot = 'WorkflowDiscussionRoot',
  WorkflowNotesRoot = 'WorkflowNotesRoot',
  Noodle = 'Noodle',
  CommunityPost = 'CommunityPost',
  WorkflowComment = 'WorkflowComment',
  WorkflowInternalNote = 'WorkflowInternalNote',
  NoodleBroadcast = 'NoodleBroadcast',
}

export enum MessageStatus {
  Draft = 'DRAFT',
  Pending = 'PENDING',
  Published = 'PUBLISHED',
  Error = 'ERROR',
}

export type Message = {
  id: string;
  sendCount: number;
  title: string | null;
  viewCount: number;
  text: string | null;
  slateAST: { children: SlateNode[] } | null;
  createdAt: Scalars['DateTime'];
  updatedAt: Scalars['DateTime'];
  editedAt?: Scalars['DateTime'] | null;
  type: MessageType;
  isDeleted: boolean;
  isDismissed: boolean;
  rank: number;
  ownerId: string | null;
  isAIGenerated: boolean;
  parentId: string | null;
  rootMessageId: string | null;
  bookingLink: string | null;
  isPinned: boolean;
  isContentHidden: boolean;
  isAvailableForFree: boolean;
  canReply: boolean;
  canReplyForFree: boolean;
  areCommentsHidden: boolean;
  messageStatus: MessageStatus;
  status: MessageStatus;
  metadata: Record<string, unknown> | null;
  businessCategories: string[] | null;
  contextType: string | null;
  contextId: string | null;
  contextTitle: string | null;
};

export type MessageOwner =
  | (Pick<Person, 'id' | 'name' | 'email'> & {
      primaryColour: { hex: string } | null;
      image: { url: string } | null;
    })
  | null;

export enum MediaStatus {
  Completed = 'COMPLETED',
  Error = 'ERROR',
  Pending = 'PENDING',
  Processing = 'PROCESSING',
  Removed = 'REMOVED',
  Uploading = 'UPLOADING',
}

export type Media = {
  id: string;
  fileSize: number | null;
  mediaStatus: MediaStatus;
  name: string | null;
  s3Id: string | null;
  s3OriginId: string | null;
  sendCount: number;
  title: string;
  transloaditS3Id: string | null;
  type: string | null;
  url: string | null;
  viewCount: number;
  vimeoThumbnailUrl: string | null;
  vimeoUri: string | null;
};

export type MessageMedia = {
  id: string;
  type: string;
  url: string | null;
  vimeoUri: string | null;
  vimeoThumbnailUrl: string | null;
};

export type GoogleOauth2Token = {
  id: Scalars['ID'];
  email: string;
};

export type CreatorCalendar = {
  id: Scalars['ID'];
  title: string;
  isEnabled: boolean;
  isPrimary: boolean;
};

// Keep in sync with CreatorCalendarAccount.Source
export enum CreatorCalendarAccountSource {
  Google = 'google',
  Outlook = 'outlook',
}

export type CreatorCalendarAccount = {
  id: Scalars['ID'];
  title: string;
  isConnected: boolean;
  source: CreatorCalendarAccountSource;
};

export type Tier = {
  id: string;
  title: string;
  description: string;
  productId?: string | null;
  isActive: boolean;
};

export type Testimonial = Pick<GraphQLModels.Testimonial, 'id'>;

export type DiscountCode = {
  id: string;
  code: string;
  percentOff: number | null;
  amountOff: number | null;
  minimumAmount: number | null;
  expirationDate: Scalars['DateTime'] | null;
  currency: string | null;
  maxRedemptions: number | null;
};

export type DiscountCodeProduct = {
  productId: string;
};

export enum ConversationStatus {
  Completed = 'COMPLETED',
  Pending = 'PENDING',
  CreatorResponseRequired = 'RESPONSE_REQUIRED',
  CustomerResponseRequired = 'CUSTOMER_RESPONSE_REQUIRED',
}

export type Conversation = {
  conversationStatus: ConversationStatus;
  id: string;
  creatorId: string;
  name: string;
  sid: string; // @deprecated
  isAiEnabled: boolean;
  endedAt?: Scalars['DateTime'] | null;
  rootMessageId: string | null;
  purchasesCount: number;
  createdAt: Scalars['DateTime'];
  lastActivityTime: Scalars['DateTime'] | null;
  lastMessage: string;
  purchasedProductTypes: NoodleProductTypes[];
  unreadCount: number;
  isScheduled: boolean;
  isSubscriber: boolean;
  isPaid: boolean;
  isLead: boolean;
  isAnonymous: boolean;
  isWorkflow: boolean;
  color: string | null;
  image: string | null;
};

export enum OrderStatuses {
  Failed = 'failed',
  Processing = 'processing',
  Succeeded = 'succeeded',
  Trialing = 'trialing',
}

export enum TransactionTypes {
  Free = 'free',
  OneTime = 'one_time',
  SubscriptionCreate = 'subscription_create',
  SubscriptionRenewal = 'subscription_renewal',
}

export type Tag = {
  id: Scalars['ID'];
  name: Scalars['String'];
};

export enum CreatorTierType {
  Enterprise = 'Enterprise',
  Free = 'Free',
  Growth = 'Growth',
  Scale = 'Scale',
  Superstar = 'Superstar',
}

export enum CreatorPayoutSchedule {
  Monthly = 'monthly',
  Weekly = 'weekly',
  Daily = 'daily',
}

export enum SourcePlatform {
  AppSumo = 'AppSumo',
  Internal = 'Internal',
}

export type AgencyTier = {
  id: string;
};

export type CreatorTier = {
  id: string;
  creatorTierType: CreatorTierType;
  title: string;
  broadcastsPerMonthLimit: number | null;
  bytesLimit: number | null;
  chatsLimit: number | null;
  leadsLimit: number | null;
  applicationFeeCharge: number;
  applicationFeeHandbook: number;
  applicationFeeInPersonSession: number;
  applicationFeeLite: number;
  applicationFeeInvoices: number;
  applicationFeeOnlineSession: number;
  applicationFeeProductGroup: number;
  applicationFeeResponse: number;
  applicationFeeSubscription: number;
  applicationFeeConsultations: number;
  canCreateCharge: boolean;
  canCreateHandbook: boolean;
  canCreateInPersonSession: boolean;
  canCreateLite: boolean;
  canCreateOnlineSession: boolean;
  canCreateProductGroup: boolean;
  canCreateResponse: boolean;
  canCreateSubscription: boolean;
  canCreateConsultation: boolean;
  upgradeUrl: string | null;
  membersLimit?: number | null;
  platform: SourcePlatform;
  payoutSchedule: CreatorPayoutSchedule;
};

export enum WORKFLOW_REFERENCE_TYPES {
  TEST_TYPE = 'test',
  PRODUCT = 'product',
}

export enum CONTEXT_TYPE {
  ADDITIONAL_INFO = 'additional-info',
  PERSON = 'person',
  SELECTION = 'selection',
}

export type Workflow = {
  creatorId: string;
  enableUscisUpdates: boolean;
  enablePacerSubmissions: boolean;
  estimatedTotalFees: number | null;
  hasGeneratedPdfs: boolean;
  id: string;
  isArchived: boolean;
  isEnabled: boolean;
  isLocked: boolean;
  name: string;
  noodleFee: number;
  publishedAt: Scalars['DateTime'] | null;
  referenceId: string | null;
  referenceType: WORKFLOW_REFERENCE_TYPES;
  canInitiateAssociatedWorkflows: boolean;
  preserveWorkflowAssignment: boolean;
  sendEmailToCreatorOnCompletion: boolean;
  sendEmailToCreatorOnFirstPayment: boolean;
  state: 'current' | 'draft' | 'stale';
  type: 'basic' | 'attorney-case';
  caseType: string | null;
  version: number;
  slug: string;
};

export type WorkflowReference = {
  id: string;
  type: WORKFLOW_REFERENCE_TYPES;
  title: string | null;
};

export type WorkflowContext = {
  id: string;
  isOptional: boolean;
  isTitleField: boolean;
  enableInvoiceView: boolean;
  rank: number;
  type: CONTEXT_TYPE;
  label: string;
  options: { slug: string; label: string }[];
  slug: string;
  expressionId: string | null;
};

export type CreatePersonContextData = {
  isCustomer: boolean;
  email: string;
  name: string;
  contactPhoneNumber?: string | null;
};

export type CreateInfoContextData = { value: string; companyId?: string };
export type CreateSelectionContextData = { slug: string };
export type CreateNotesContextData = { notes: string };

export enum VARIABLE_TYPE {
  PERSON_NAME = 'person-name',
  ENTERED_VALUE = 'entered-value',
}

export type WorkflowContextVariable = {
  id: string;
  workflowContextId: string;
  variableName: string;
  type: VARIABLE_TYPE;
};

export type UserWorkflowContext = {
  id: string;
  type: CONTEXT_TYPE;
  label: string;
  rank: number;
  value: string;
  details?: Pick<Person, 'id' | 'name' | 'email' | 'phoneNumber'> | null;
};

export enum StepType {
  TRIGGER = 'trigger',
  ACTION = 'action',
}

export enum STEP_STATUS {
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  IN_PROGRESS = 'in-progress',
}

export enum WORKFLOW_ACTIONS {
  SEND_MESSAGE = 'send-message',
  GENERATE_ALL_WORKFLOW_PDFS = 'generate-all-workflow-pdfs',
  SEND_EMAIL = 'send-email',
  SEND_DOCUMENT_REQUEST = 'send-document-request',
  TEST_ACTION = 'test-action',
  SEND_E_SIGNATURE_REQUEST = 'send-e-signature-request',
}

export enum WORKFLOW_TRIGGERS {
  ALL_DEPENDENCIES = 'all-dependencies',
  CART_ABANDONED = 'cart-abandoned',
  CUSTOM_TERMS_AGREED = 'custom-terms-agreed',
  DOCUMENTS_UPLOADED = 'documents-uploaded',
  FORM_REQUEST_COMPLETED = 'form-request-completed',
  CREDIT_REPORT_COMPLETED = 'credit-report-completed',
  INITIATE_CTA = 'initiate-cta',
  INITIATE_THREAD = 'initiate-thread',
  PRODUCT_PURCHASED = 'product-purchased',
  PURCHASE_INITIATED = 'purchase-initiated',
  TEST_TRIGGER = 'test-trigger',
  UPCOMING_BOOKING = 'upcoming-booking',
  USER_WORKFLOW_CTA = 'user-workflow-cta',
  BOOKING_SCHEDULED = 'booking-scheduled',
  E_SIGNATURE_REQUEST_CREATED = 'e-signature-request-created',
  E_SIGNATURE_REQUEST_COMPLETED = 'e-signature-request-completed',
}

export enum EXTERNAL_DEPENDENCY_REFERENCE_TYPES {
  DOCUMENT_REQUEST_USER = 'document-request-user',
  FORM_REQUEST = 'form-request',
}

export type UserWorkflow = {
  canceledAt: Scalars['DateTime'] | null;
  createdAt: Scalars['DateTime'];
  endedAt: Scalars['DateTime'] | null;
  hasAssignedTasks: boolean;
  id: string;
  lastActivityAt: Scalars['DateTime'];
  messageCount: number;
  nextFollowupTime: Scalars['DateTime'] | null;
  noodleFee: number;
  numCompletedTasks: number;
  numPendingClientTasks: number;
  numStepsTaken: number;
  numTotalTasks: number;
  numWorkflowSteps: number;
  personId: string;
  completedAt: Scalars['DateTime'] | null;
  status: string;
  title: string;
  uscisReceiptNumber: string | null;
  pausedUntil: Scalars['DateTime'] | null;
  filedAt: Scalars['DateTime'] | null;
  progress: number;
  tagText: string | null;
  tagIcon: string | null;
  companyId: string | null;
  personAttorneyId: string | null;
  personAttorneyPersonId: string | null;
  enablePacerSubmissions: boolean;
  districtTemplateId: string | null;
  lastStatusChangeAt: Scalars['DateTime'] | null;
  lastAssignmentChangeAt: Scalars['DateTime'];
};

export type WorkflowConversation = {
  id: string;
  rootMessageId: string | null;
};

export const UserWorkflowCollaboratorSourceEnum = z.enum([
  'manually-added',
  'customer',
  'organization-member',
  'creator',
  'creator-team-member',
  'context-person',
]);
export type UserWorkflowCollaborator = {
  id: string;
  source: z.infer<typeof UserWorkflowCollaboratorSourceEnum>;
  canAssign: boolean;
  canInviteCollaborators: boolean;
  canReceiveCustomerNotifications: boolean;
  canHavePermissionsChanged: boolean;
  name: string | null;
  color: string | null;
  logo: string | null;
  roleLabel?: string | null;
  email: string | null;
  personId: string;
  contactPhoneNumber: string | null;
};

export type WorkflowStepOwnerAssignment = Array<{
  roleId: string;
}> | null;

export type WorkflowStep = {
  id: string;
  type: StepType;
  data: unknown;
  eventName: WORKFLOW_ACTIONS | WORKFLOW_TRIGGERS;
  workflowId: string;
  isRequired: boolean;
  title: string | null;
  workflowStatus: string | null;
  ownerAssignment: WorkflowStepOwnerAssignment;
};

export enum PAYMENT_GATE_TYPES {
  MINIMUM = 'minimum',
  PERCENTAGE = 'percentage',
}

export enum PAYMENT_GATE_STATUS {
  PENDING = 'pending',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
}

/**
 * Template-side authoring config for a payment gate. Serialized into
 * `WorkflowStep.data.paymentGates: PaymentGateConfig[]` and validated
 * on save by noodle-api (CORE-1280, CORE-1314, CORE-1315).
 */
export type PaymentGateConfig = {
  /** References a WorkflowInvoice.id on this workflow template. */
  invoiceTemplateRef: string;
  gateType: PAYMENT_GATE_TYPES;
  /** Cents. Required when gateType === 'minimum'. */
  minimumAmount?: number;
  /** 1–100. Required when gateType === 'percentage'. */
  minimumPercentage?: number;
  /**
   * References an Expression row. When set, the gate is only materialized
   * at step kickoff when the expression evaluates truthy against the user
   * workflow's context. Populated from the paired message attachment's
   * condition widget at save time.
   */
  displayConditionExpressionId?: string;
  /**
   * Assignment scope for the gate, populated from the paired message
   * attachment's "Assigned to" widget at save time. Runtime semantics
   * (follow-ups, blocking behavior) are deferred — see CORE-1315.
   */
  assignTo?: AssignToData[];
  /**
   * Stable template-side pairing key shared with the PAYMENT_GATE chip's
   * attachment.referenceId. Used at runtime to rewrite the chip ref to the
   * materialized PaymentGate.id (CORE-1313).
   */
  templateAttachmentRef?: string;
};

export type StepTaken = {
  id: string;
  status: STEP_STATUS;
  stepId: string;
  updatedAt: Scalars['DateTime'];
  createdAt: Scalars['DateTime'];
  lastActivityAt: Scalars['DateTime'];
};

export enum MESSAGE_ATTACHMENT_REFERENCE_TYPES {
  DOCUMENT_REQUEST_TEMPLATE = 'document-request-template',
  DOCUMENT_REQUEST_USER = 'document-request-user',
  CREDIT_REPORT = 'credit-report',
  PAYMENT_LINK = 'payment-link',
  FORM_REQUEST_TEMPLATE = 'form-request-template',
  FORM_REQUEST = 'form-request',
  INVOICE = 'invoice',
  INVOICE_REQUEST = 'invoice-request',
  CUSTOM_TERMS_TEMPLATE = 'custom-terms-template',
  CUSTOM_TERMS = 'custom-terms',
  BOOKING_PRICE = 'booking-price',
  BOOKING = 'booking',
  BANK_ACCOUNT_INTEGRATION = 'bank-account-integration',
  E_SIGNATURE_REQUEST = 'e-signature-request',
  DOCUMENT = 'document',
  PAYMENT_GATE = 'payment-gate',
}

export type MessageAttachment = {
  id: string;
  referenceId: string;
  referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES;
  messageId: string;
  canAccess: boolean;
  title: string | null; // @todo noodle-api isn't sending this... remove from here or start sending?
  createdAt: Scalars['DateTime'];
  updatedAt: Scalars['DateTime'];
  assigneeIds: string[];
  nextFollowupTime: Date;
  isFollowupEnabled: boolean;
  lastActivityAt: Scalars['DateTime'];
  completedAt: Scalars['DateTime'] | null;
  integrationSlugs: string[];
};

export enum FollowupCadenceUnits {
  None = 'none',
  Days = 'days',
  Hours = 'hours',
  Minutes = 'minutes',
  Weeks = 'weeks',
}

export type Customer = {
  id: string;
  personId: string;
  isVerified: boolean;
  creatorId: string;
  createdAt: Scalars['DateTime'];
  stripeCustomerId: string | null;
  canCreateOrganization: boolean;
  optedIntoBroadcasts: boolean;
  followupCadence: number | null;
  followupCadenceUnits: FollowupCadenceUnits | null;
  followupCadencePercentage: number | null;
  optedIntoSms: boolean | null;
  dateOfBirth: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  line1: string | null;
  line2: string | null;
};

export type AssignToPersonType = 'context' | 'team-member' | 'customer' | 'workflow-owner';
export type AssignToData = {
  personType: AssignToPersonType;
  referenceId: string;
};

export type WorkflowStepEventNameAndData =
  // Triggers
  | {
      eventName: WORKFLOW_TRIGGERS.TEST_TRIGGER;
      data: null;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.PRODUCT_PURCHASED;
      data: null;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.CART_ABANDONED;
      data: null;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.UPCOMING_BOOKING;
      data: null;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.DOCUMENTS_UPLOADED;
      data: null;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.USER_WORKFLOW_CTA;
      data: {
        title?: string;
      };
    }
  | {
      eventName: WORKFLOW_TRIGGERS.FORM_REQUEST_COMPLETED;
      data: unknown;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.CUSTOM_TERMS_AGREED;
      data: null;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.E_SIGNATURE_REQUEST_CREATED;
      data: null;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.E_SIGNATURE_REQUEST_COMPLETED;
      data: null;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.INITIATE_CTA;
      data: {
        CTA: string;
        description: string;
        initiatePrompt?: string;
        title: string; // @deprecated in favor of workflowStep.title
      };
    }
  | {
      eventName: WORKFLOW_TRIGGERS.INITIATE_THREAD;
      data: unknown;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.PURCHASE_INITIATED;
      data: unknown;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.ALL_DEPENDENCIES;
      data: unknown;
    }
  | {
      eventName: WORKFLOW_TRIGGERS.BOOKING_SCHEDULED;
      data: unknown;
    }
  // Actions
  | {
      eventName: WORKFLOW_ACTIONS.SEND_MESSAGE;
      data: {
        message: Message & {
          attachments: Array<MessageAttachment & { assignTo: AssignToData[] }>;
        };
      }; // @todo
    }
  | {
      eventName: WORKFLOW_ACTIONS.SEND_EMAIL;
      data: unknown; // @todo
    }
  | {
      eventName: WORKFLOW_ACTIONS.SEND_DOCUMENT_REQUEST;
      data: unknown; // @todo
    }
  | {
      eventName: WORKFLOW_ACTIONS.GENERATE_ALL_WORKFLOW_PDFS;
      data: unknown; // @todo
    }
  | {
      eventName: WORKFLOW_ACTIONS.TEST_ACTION;
      data: unknown; // @todo
    };

export type InitiateWorkflowCTA = {
  id: string;
  CTA: string;
  description: string;
  title: string;
  initiatePrompt?: string;
  workflowId: string;
  workflowEnabled: boolean;
};

export enum BankAccountIntegrationRequestStatus {
  COMPLETED = 'completed',
  IN_PROGRESS = 'in-progress',
  INITIATED = 'initiated',
  SKIPPED = 'skipped',
}

export type MessageAttachmentBankAccountIntegrationData = {
  userWorkflowId: string | null;
  id: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  creatorId: string;
  status: BankAccountIntegrationRequestStatus;
  completedAt: Date | null;
  skippedAt: Date | null;
};

export enum ESignatureRequestStatus {
  INITIATED = 'initiated',
  DRAFT = 'draft',
  FORM_SUBMITTED = 'form-submitted', // provisional client status
  SENT = 'sent',
  PARTIAL = 'partial',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
  FAILED = 'failed',
  VOIDED = 'voided',
}

export type MessageAttachmentESignatureRequestData = {
  eSignatureRequestId: string;
  status: ESignatureRequestStatus;
  completedAt: Date | null;
  skippedAt: Date | null;
  uploadedDocumentId: string | null;
  signedDocumentId: string | null;
  fileName: string | null;
  packetEid: string | null;
  personId: string | null;
  userWorkflowId: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  creatorId: string;
  anvilCertificateForSignedDocumentId: string | null;
};

/** Data shape for a document attachment on a message. */
export type MessageAttachmentDocumentData = {
  fileName: string;
  mimeType: string;
  fileSize: number;
};

export type CreateMessageAttachment =
  | {
      assignTo: AssignToData[];
      data: { userWorkflowId?: string };
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_TEMPLATE;
    }
  | {
      assignTo: AssignToData[];
      data: unknown;
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_USER;
    }
  | {
      assignTo: AssignToData[];
      data: {
        workflowReferenceId: string;
        userWorkFlowId?: string;
      };
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.PAYMENT_LINK;
    }
  | {
      assignTo: AssignToData[];
      data: { userWorkflowId?: string };
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.CREDIT_REPORT;
    }
  | {
      assignTo: AssignToData[];
      data: { userWorkflowId?: string };
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST_TEMPLATE;
    }
  | {
      assignTo: AssignToData[];
      data: { userWorkflowId?: string };
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS_TEMPLATE;
    }
  | {
      assignTo: AssignToData[];
      data: unknown;
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS;
    }
  | {
      assignTo: AssignToData[];
      data: {
        workflowReferenceId?: string;
        userWorkflowId?: string;
      };
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE_REQUEST;
    }
  | {
      assignTo: AssignToData[];
      data: unknown;
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE;
    }
  | {
      assignTo: AssignToData[];
      data: unknown;
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST;
    }
  | {
      assignTo: AssignToData[];
      data: { userWorkflowId?: string };
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.BOOKING_PRICE;
    }
  | {
      assignTo: AssignToData[];
      data: { userWorkflowId: string | null };
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.BANK_ACCOUNT_INTEGRATION;
    }
  | {
      assignTo: AssignToData[];
      data: unknown;
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.E_SIGNATURE_REQUEST;
    }
  | {
      assignTo: AssignToData[];
      data: MessageAttachmentDocumentData;
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT;
    }
  | {
      assignTo: AssignToData[];
      data: unknown;
      expressionId?: string | null;
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.PAYMENT_GATE;
    };

export enum FormProviders {
  Anvil = 'anvil',
  Typeform = 'typeform',
  Noodle = 'noodle',
}

export type FormRequestTemplateTag = {
  label: string;
  slug: string;
};

export type FormRequestTemplate = {
  autoAssignCreatorInWorkflows: boolean;
  autoAssignCustomerInWorkflows: boolean;
  canAutoComplete: boolean;
  completionRequiresAllRequiredFields: boolean;
  creatorId: string;
  description: string | null;
  followupCadence: number;
  followupCadenceUnits: FollowupCadenceUnits;
  id: string;
  inheritanceScheme: 'none' | 'creator' | 'customer';
  isFollowupEnabled: boolean;
  name: string;
  provider: FormProviders;
  providerLocator: string;
  tags: FormRequestTemplateTag[] | null;
  enableAiSummary: boolean;
  usesCasebuilderQuestionnaire: boolean;
  aiSummaryVoice: string | null;
  enableReviewState: boolean;
  emailNotificationsEnabled: boolean;
  isCaseDocument: boolean;
};

export enum FormStatus {
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
  SUBMITTED_FOR_REVIEW = 'submitted-for-review',
}

// copied from @typeform/api-client Typeform.Response
type TypeformDocumentData = {
  [key: string]: any;
};

// copied from @typeform/api-client Typeform.Response
export type FormRequestResponseTypeform = {
  /**
   * Unique ID for the response. Note that `response_id` values are unique per form but are not unique globally.
   */
  response_id?: string;
  /**
   * Time of the form landing. In ISO 8601 format, UTC time, to the second, with T as a delimiter between the date and time.
   */
  landed_at?: string;
  /**
   * Time that the form response was submitted. In ISO 8601 format, UTC time, to the second, with T as a delimiter between the date and time.
   */
  submitted_at?: string;
  /**
   * Metadata about a client's HTTP request.
   */
  metadata?: {
    user_agent?: string;
    /**
     * Derived from user agent
     */
    platform?: string;
    referer?: string;
    /**
     * Ip of the client
     */
    network_id?: string;
  };
  hidden?: TypeformDocumentData;
  /**
   * Subset of a complete form definition to be included with a submission.
   */
  definition?: {
    fields?: {
      id?: string;
      type?: string;
      title?: string;
      description?: string;
      allow_multiple_selections?: boolean; // added manually, not sure why it's not in @typeform/api-client
    }[];
  };
  answers?: {
    field?: {
      /**
       * The unique id of the form field the answer refers to.
       */
      id?: string;
      /**
       * The field's type in the original form.
       */
      type?: string;
      /**
       * The reference for the question the answer relates to. Use the `ref` value to match answers with questions.
       * The Responses payload only includes `ref` for the fields where you specified them when you created the form.
       */
      ref?: string;
      /**
       * The form field's title which the answer is related to.
       */
      title?: string;
    };
    /**
     * The answer-field's type.
     */
    type?: 'choice' | 'choices' | 'date' | 'email' | 'url' | 'file_url' | 'number' | 'boolean' | 'text' | 'payment';
    /**
     * Represents single choice answers for dropdown-like fields.
     */
    choice?: {
      label?: string;
      other?: string;
    };
    /**
     * Represents multiple choice answers.
     */
    choices?: {
      labels?: string[];
      other?: string;
    };
    date?: string;
    email?: string;
    file_url?: string;
    number?: number;
    boolean?: boolean;
    text?: string;
    url?: string;
    payment?: {
      amount?: string;
      last4?: string;
      name?: string;
    };
  }[];
  calculated?: {
    score?: number;
  };
};

export type AnvilPhoneValue = {
  num: string;
  region: string;
};

export type AnvilFullNameValue = {
  firstName: string;
  lastName: string;
  mi: string;
};

export type AnvilAddressValue = {
  city: string;
  country: string;
  state: string;
  street1: string;
  street2: string;
  zip: string;
};

export type AnvilCompoundSelectValue = {
  k: string;
  v: string;
};

export type AnvilArrayValue = Record<
  string,
  {
    type: string;
    value: unknown;
  }
>[];

export type AnvilValue =
  | AnvilPhoneValue
  | AnvilAddressValue
  | AnvilFullNameValue
  | AnvilCompoundSelectValue
  | AnvilArrayValue
  | Array<
      Record<
        string,
        {
          type: string;
          value: AnvilValue;
        }
      >
    >
  | unknown;

export type AnvilSubmission = {
  /** The `eid` is the object identifier. */
  eid: string;
  /** Possible values: `created`, `in-progress`, `waiting-to-sign`, `user-signed-and-waiting`, `users-turn-to-sign-ui`, `users-turn-to-sign-email`, `someone-else-signed-and-waiting`, `completed` */
  status: string;
  forge: {
    eid: string;
    id: number;
  } | null;
  /** The payload resolved with field aliases and field information. If you need to fetch the submission payload, use this. */
  resolvedPayload: Record<
    string,
    {
      id: string;
      type: string;
      label: string;
      value: AnvilValue;
    }
  >;
  /** The raw payload */
  payload: Record<
    string,
    {
      type: string;
      value: AnvilValue;
    }
  >;
  /** The raw payload without types */
  payloadValue: Record<string, AnvilValue>;
  currentStep: number;
  totalSteps: number;
  continueURL: string;
  // weldData?: Maybe<WeldData>
  // forge?: Maybe<Forge>
  // user?: Maybe<User>
  // signer?: Maybe<Signer>
  // reviewData?: Maybe<Scalars["JSON"]>
  completionPercentage: number;
  isExcluded: boolean;
  touchedByUser: boolean;
  requestMeta: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  completedAt: string;
};

export type AnvilFormPage = {
  id: string;
  title: string;
  fields: Array<{
    fields?: Array<{
      id: string;
      type: string;
      label: string;
      options?: Array<{ k: string; v: string }>;
    }>;
    id: string;
    options?: {
      boxLabel?: string | null;
    };
    type: string;
    label: string;
  }>;
};

export type FormRequestResponseAnvil = {
  pages: Array<AnvilFormPage>;
  // The full submission isn't sent in the webhook
  submission: Pick<AnvilSubmission, 'eid' | 'completedAt' | 'status' | 'payload' | 'resolvedPayload'>;
};

export enum INVOICE_STATUS {
  IS_GENERATING = 'is-generating', // The amount/price has not been assigned to the invoice yet.
  PAID = 'paid', // Fully paid
  FINAL_PAYMENT_INITIATED = 'payment-initiated', // Final payment is underway, but not yet completed.
  IN_PROGRESS = 'in-progress', // Amount has been set, but not fully paid yet.
  PAYMENT_REQUESTED = 'payment-requested', // Payment request sent to payment processor, but not confirmed yet. Don't allow other payments while in this state.
  PAYMENT_FAILED = 'payment-failed', // Last payment had an error (maybe not wanted???).
  VOIDED = 'voided', // Need to change the price/amount
  SKIPPED = 'skipped', // Do not attempt to collect.
  EDITED = 'edited',
}

export type InvoiceTemplate = {
  id: string;
  creatorId: string;
  priceId: string | null;
  productId: string | null;
  title: string;
  minimumAmount: number | null;
  followupCadence: number;
  followupCadenceUnits: FollowupCadenceUnits;
  autoAssignCreatorInWorkflows: boolean;
  autoAssignCustomerInWorkflows: boolean;
  isFollowupEnabled: boolean;
  isExemptFromApplicationFee: boolean;
  isPartialPaymentEnabled: boolean;
  isCreditCardPaymentEnabled: boolean;
  isDebitCardPaymentEnabled: boolean;
  isDebitCardPaymentRequiredForCustomer: boolean;
  isAchPaymentEnabled: boolean;
  passProcessingFeesToCustomer: boolean;
  currency: string;
  secondaryStripeAccountId: string | null;
  confidoBankAccountId: string | null;
  customTermsVariableName: string | null;
  slug: string;
  areClientModificationDisabled: boolean;
  modificationNotAllowedMessage: string;
  emailNotificationsEnabled: boolean;
  maxPaymentPlanMonths: number | null;
};

export type Invoice = {
  templateId: string;
  amountDue: number;
  amountPaid: number;
  amountPending: number;
  amountToBeRefunded?: number;
  amountTotal: number;
  amountRefunded: number;
  id: string;
  currency: Currency;
  createdAt: Scalars['DateTime'];
  creatorId: string;
  personId: string;
  previousVersionInvoiceIds: string[] | null;
  priceId: string | null;
  productId: string | null;
  documentId: string | null;
  workflowReferenceId: string | null;
  status: INVOICE_STATUS;
  fallbackTitle: string | null;
  title: string | null;
  userWorkflowId: string | null;
  completedAt: Scalars['DateTime'] | null;
  initiatedAt: Scalars['DateTime'] | null;
  skippedAt: Scalars['DateTime'] | null;
  isExemptFromApplicationFee: boolean;
  isPartialPaymentEnabled: boolean;
  isCreditCardPaymentEnabled: boolean;
  isDebitCardPaymentEnabled: boolean;
  isDebitCardPaymentRequiredForCustomer: boolean;
  isAchPaymentEnabled: boolean;
  passProcessingFeesToCustomer: boolean;
  canBePaid: boolean;
  hasPayPermission: boolean;
  hasInvoiceViewPermission: boolean;
  isFollowupEnabled: boolean;
  secondaryStripeAccountId: string | null;
  confidoPaymentLink: string | null;
  confidoBankAccountId: string | null;
  canAccess: boolean;
  minimumAmount: number | null;
  maxPaymentPlanMonths: number | null;
  areClientModificationDisabled: boolean;
  modificationNotAllowedMessage: string;
};

export type InvoicePermission = {
  id: string;
  invoiceId: string;
  personId: string;
};

export type InvoiceLineItem = {
  id: string;
  creatorId: string;
  invoiceId: string;
  item: string;
  price: number | null;
  description: string | null;
  quantity: number;
  priceId: string | null;
  customTermsVariableName: string | null;
};

export type InvoiceTemplateLineItem = {
  id: string;
  creatorId: string;
  invoiceTemplateId: string;
  item: string;
  price: number | null;
  description: string | null;
  quantity: number;
  priceId: string | null;
  customTermsVariableName: string | null;
};

export type CustomTerms = {
  id: string;
  termsText: string | null;
  agreedAt: Scalars['DateTime'] | null;
  skippedAt: Scalars['DateTime'] | null;
  documentId: string | null;
  certificateDocumentId: string | null;
  userWorkflowId: string | null;
  personId: string;
  isGenerating: boolean;
  usesHandwrittenSignature: boolean;
  manualAttorneySignature: boolean;
};

export type StatusIcons =
  | 'Archive'
  | 'Binoculars'
  | 'CheckCircle'
  | 'Clock'
  | 'Empty'
  | 'Files'
  | 'Globe'
  | 'HourglassHigh'
  | 'Lightbulb'
  | 'Lock'
  | 'Microphone'
  | 'PaymentCheck'
  | 'PencilSimple'
  | 'Question'
  | 'Signature'
  | 'Star'
  | 'Tag'
  | 'Trash';

export type CustomStatus = {
  colour: string;
  creatorId: string;
  icon: StatusIcons;
  id: string;
  title: string;
  slug: string;
  usesArchiveBehavior: boolean;
  disablesFollowups: boolean;
  archivedAt: Scalars['DateTime'] | null;
};

export type CustomTermsPermission = {
  id: string;
  customTermsId: string;
  personId: string;
};

export type TypedCustomTermsDynamicVariable = {
  id: string;
  name: string;
  type: CustomTermsDynamicVariableType;
  filledAt?: string | null;
};

export type CustomTermsTemplate = {
  id: string;
  title?: string | null;
  templateContent?: string | null;
  companySignatory?: string | null;
  customTermsDynamicVariables: TypedCustomTermsDynamicVariable[];
  isFollowupEnabled: boolean;
  autoAssignCreatorInWorkflows: boolean;
  autoAssignCustomerInWorkflows: boolean;
  usesHandwrittenSignature: boolean;
  products: Pick<Product, 'id'>[];
  signatorySignature: string | null;
  enableCompletedEmailNotification: boolean;
  manualAttorneySignature: boolean;
  enableAttorneySignatureEmailNotification: boolean;
};

export type CreditReport = {
  id: string;
  creatorId: string;
  userWorkflowId: string | null;
  productId: string | null;
  completedAt: Scalars['DateTime'] | null;
  skippedAt: Scalars['DateTime'] | null;
  status: 'initiated' | 'approval-request-sent' | 'completed';
};

export type CreditReportPermission = {
  id: string;
  personId: string;
  creditReportId: string;
};

export type CreditReportResponse = {
  id: string;
  creditReportId: string;
  personId: string;
  bureau: BureauType;
  requestId: string;
  response: unknown; // jsonb
  pdfRequestId: string | null;
  previousBankruptciesResponse: unknown; // jsonb
  previousBankruptciesDocId: string | null;
  propertyDataResponse: unknown; // jsonb
  propertyDataDocId: string | null;
  crsUserId: string | null;
  slug: 'main-debtor' | 'second-debtor';
};

export type BankAccountIntegrationPermission = {
  id: string;
  personId: string;
  bankAccountIntegrationId: string;
};

export type BankAccountDocument = {
  id: string;
  documentId: string;
  documentName: string;
  institutionName: string;
  accountType: string | null;
  type: 'manual' | 'plaid';
  bankAccountIntegrationId: string;
  userId: string | null;
  accountSubType: string | null;
  accountName: string | null;
  accountId: string | null;
  statementId: string | null;
  contentHash: string | null;
  extractedData: unknown;
  createdAt: Date;
};

export type BankAccountIntegrationData = {
  id: string;
  creatorId: string;
  userWorkflowId: string | null;
  status: BankAccountIntegrationRequestStatus;
  completedAt: Date | null;
  skippedAt: Date | null;
  documents?: BankAccountDocument[];
};

export type FormResponseQuestionField = {
  question: {
    title: string;
  };
};

interface BaseField<T> {
  type: string;
  answer: T | null;
}

export type FormResponseBooleanField = BaseField<boolean> & { type: 'boolean' };
export type FormResponseDateOnlyField = BaseField<string> & { type: 'date-only' | 'date' };
export type FormResponseEmailField = BaseField<string> & { type: 'email' };
export type FormResponseIntegerField = BaseField<number> & { type: 'integer' };
export type FormResponseNumberField = BaseField<number> & { type: 'number' };
export type FormResponsePercentField = BaseField<number> & { type: 'percent' };
export type FormResponseDollarField = BaseField<number> & { type: 'dollar' };
export type FormResponseTextField = BaseField<string> & {
  type: 'long-text' | 'short-text' | 'phone-number' | 'numeric';
};
export type FormResponseSSNField = BaseField<string> & { type: 'ssn' };
export type FormResponseUrlField = BaseField<string> & { type: 'url' };
export type FormResponseYesNoField = BaseField<boolean> & { type: 'yes-no' };
export type FormResponseUnknownField = BaseField<unknown> & { type: 'unknown' };
export type FormResponseCurrencyField = BaseField<CurrencyValue> & { type: 'currency' };
export type FormResponseNameField = BaseField<Name> & { type: 'name' };
export type FormResponseUSAddressField = BaseField<USAddress> & { type: 'address-us' };
export type FormResponseIntlAddressField = BaseField<IntlAddress> & { type: 'address-international' };

export type FormResponseMultipleChoiceField =
  | {
      type: 'multiple-choice';
      allowMultipleSelections: false;
      answer: string | null;
    }
  | {
      type: 'multiple-choice';
      allowMultipleSelections: true;
      answer: string[] | null;
    }
  | {
      type: 'multi-select';
      allowMultipleSelections: true;
      answer: { key: string; label: string }[] | null;
    }
  | {
      type: 'single-select';
      allowMultipleSelections: false;
      answer: { key: string; label: string } | null;
    }
  | {
      type: 'selection';
      allowMultipleSelections: false;
      answer: { key: string; label: string } | null;
    };

export type FormResponseAnswerField =
  | FormResponseBooleanField
  | FormResponseDateOnlyField
  | FormResponseEmailField
  | FormResponseIntegerField
  | FormResponseMultipleChoiceField
  | FormResponseNumberField
  | FormResponsePercentField
  | FormResponseDollarField
  | FormResponseTextField
  | FormResponseSSNField
  | FormResponseUnknownField
  | FormResponseUrlField
  | FormResponseYesNoField
  | FormResponseCurrencyField
  | FormResponseNameField
  | FormResponseUSAddressField
  | FormResponseIntlAddressField
  | {
      type: 'array';
      answer: Array<
        Array<
          FormResponseAnswerField & {
            id: string;
            question: { title: string };
          }
        >
      > | null;
    };

export type FormResponseField = FormResponseAnswerField & FormResponseQuestionField & { id: string };

export type FormRequestResponseNoodle = Array<FormResponseField>;

/* Fixing caused errors...
type NoodleQuestionnaireResponseQuestionAnswer = {
  id: string;
  question: {
    title: string;
  };
  allowMultipleSelections?: boolean;
  answer: unknown;
  type: string;
};

export type FormRequestResponseNoodle = Array<NoodleQuestionnaireResponseQuestionAnswer>;
*/
export type FormRequest = {
  id: string;
  formTemplateId: string;
  creatorId: string;
  personId: string;
  status: FormStatus;
  url: string;
  response: FormRequestResponseTypeform | FormRequestResponseAnvil | FormRequestResponseNoodle | null;
  hasEditPermission: boolean;
  hasViewPermission: boolean;
  completedAt: Scalars['DateTime'] | null;
  skippedAt: Scalars['DateTime'] | null;
  userWorkflowId: string | null;
  formReferenceId: string;
  aiSummary: string | null;
  reOpenMessage: string | null;
};

export type FormRequestPermission = {
  id: string;
  formRequestId: string;
  personId: string;
};

export enum ESignatureSignersStatus {
  VIEWED = 'viewed',
  SENT = 'sent',
  SIGNER_SIGN = 'signerSign', // event emitted from anvil iframe when signer clicks 'Signs the document'
  SIGNER_COMPLETE = 'signerComplete', // event emitted from anvil iframe emitted shortly after 'signerSigned' event confirming success on anvil's end
  SIGNER_ERROR = 'signerError', // event emitted from anvil iframe when signer fails to sign the document
  COMPLETED = 'completed', // event emitted from anvil iframe when signer completes the document
}

export type SignerInput = {
  id: string; // this is linked to our personId
  name: string;
  email: string;
  routingOrder: number;
  signerType: 'embedded';
  enableEmails?: boolean;
  acceptEachField?: boolean;
  showFinishSigningButton?: boolean;
  redirectURL?: string;
};

export type ESignatureSigner = {
  id: string;
  eSignatureRequestId: string;
  personId: string | null;
  signerEid: string | null;
  status: ESignatureSignersStatus;
  name: string | null;
  email: string | null;
  routingOrder: number | null;
  createdAt: Scalars['DateTime'];
  updatedAt: Scalars['DateTime'];
  deletedAt: Scalars['DateTime'] | null;
  person?: Pick<Person, 'id' | 'name' | 'email'> | null;
  eSignatureSigners?: ESignatureSigner[];
};

export type ESignatureRequest = {
  id: string;
  creatorId: string;
  personId: string | null;
  packetEid: string | null;
  uploadedDocumentId: string | null;
  signedDocumentId: string | null;
  anvilCertificateForSignedDocumentId: string | null;
  fileName: string | null;
  status: ESignatureRequestStatus;
  completedAt: Scalars['DateTime'] | null;
  skippedAt: Scalars['DateTime'] | null;
  userWorkflowId: string;
  createdAt: Scalars['DateTime'];
  updatedAt: Scalars['DateTime'];
  deletedAt: Scalars['DateTime'] | null;
  eSignatureSigners: ESignatureSigner[];
};

export type ESignatureRequestPermission = {
  id: string;
  eSignatureRequestId: string;
  personId: string;
};

export type MessageAttachmentDocumentRequestData = Pick<
  DocumentApiModels.DocumentRequestUser,
  'id' | 'status' | 'userWorkflowId'
> & {
  documentRequest: Pick<DocumentApiModels.DocumentRequest, 'id' | 'title' | 'isForCreator' | 'type'>;
  actionRequiredFiles: number;
  uploadedRequiredFiles: number;
  totalRequiredFiles: number;
  uploadedFiles: number;
  totalFiles: number;
};

export type MessageAttachmentFormRequestData = Pick<
  FormRequest,
  'id' | 'formTemplateId' | 'formReferenceId' | 'personId' | 'creatorId' | 'status' | 'url' | 'skippedAt'
> & {
  template: Pick<
    FormRequestTemplate,
    'id' | 'name' | 'creatorId' | 'provider' | 'providerLocator' | 'usesCasebuilderQuestionnaire'
  >;
};

export type MessageAttachmentCreditReportData = CreditReport & {
  responses: Array<{
    creditReportResponseId: string;
    isCompleted: boolean;
    needsCreatorAction: boolean;
    personId: string;
  }>;
};

export type MessageAttachmentPaymentLinkData = {
  price: Pick<Price, 'id' | 'priceTitle'> & {
    product?: Pick<Product, 'id' | 'slug' | 'isActive' | 'title'> & {
      productTypes?: Pick<ProductType, 'title' | 'noodleProductType'>[];
      creator?: Pick<Creator, 'id' | 'slug' | 'name'> & {
        image?: Pick<Asset, 'url'> | null;
      };
    };
  };
  workflowReferenceId?: string;
};

export type MessageAttachmentInvoiceRequestData = {
  isEmptyInvoice?: boolean;
  fallbackTitle?: string | null;
  workflowReferenceId?: string;
  userWorkflowId?: string;
};

export type MessageAttachmentCustomTermsData = {
  customTerms: Pick<CustomTerms, 'id' | 'termsText' | 'agreedAt' | 'skippedAt' | 'documentId' | 'isGenerating'>;
  template: Pick<CustomTermsTemplate, 'id' | 'title'> & {
    creator: Pick<Creator, 'id' | 'slug'>;
  };
};

export type MessageAttachmentInvoiceData = {
  invoice: Pick<
    Invoice,
    | 'amountDue'
    | 'amountPaid'
    | 'amountPending'
    | 'amountTotal'
    | 'amountRefunded'
    | 'canBePaid'
    | 'creatorId'
    | 'currency'
    | 'documentId'
    | 'fallbackTitle'
    | 'id'
    | 'isPartialPaymentEnabled'
    | 'areClientModificationDisabled'
    | 'modificationNotAllowedMessage'
    | 'personId'
    | 'productId'
    | 'status'
    | 'title'
  >;
};

export type MessageAttachmentBookingData = {
  booking: Pick<Booking, 'id' | 'startAt' | 'endAt' | 'priceId' | 'productId' | 'skippedAt' | 'teamMemberPersonId'> & {
    price: Pick<Price, 'id' | 'priceTitle'> & {
      product: Pick<Product, 'id' | 'title'>;
    };
    person: Pick<Person, 'id' | 'name'> & {
      primaryColour?: { hex: string } | null;
      image?: { url: string } | null;
    };
    creator: Pick<Creator, 'id' | 'timezone'>;
    teamMemberPerson:
      | (Pick<Person, 'id' | 'name'> & {
          primaryColour?: { hex: string } | null;
          image?: { url: string } | null;
        })
      | null;
  };
};

export type MessageAttachmentWithTypedData =
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_USER;
      data: MessageAttachmentDocumentRequestData;
      expressionId?: string | null;
    }
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST;
      data: MessageAttachmentFormRequestData;
      expressionId?: string | null;
    }
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.CREDIT_REPORT;
      data: MessageAttachmentCreditReportData;
      expressionId?: string | null;
    }
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.PAYMENT_LINK;
      data: MessageAttachmentPaymentLinkData;
      expressionId?: string | null;
    }
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE;
      data: MessageAttachmentInvoiceData;
      expressionId?: string | null;
    }
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE_REQUEST;
      data: MessageAttachmentInvoiceRequestData;
      expressionId?: string | null;
    }
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS;
      data: MessageAttachmentCustomTermsData;
      expressionId?: string | null;
    }
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.BOOKING;
      data: MessageAttachmentBookingData;
      expressionId?: string | null;
    }
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.BANK_ACCOUNT_INTEGRATION;
      data: MessageAttachmentBankAccountIntegrationData;
      expressionId?: string | null;
    }
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.E_SIGNATURE_REQUEST;
      data: MessageAttachmentESignatureRequestData;
      expressionId?: string | null;
    }
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT;
      data: MessageAttachmentDocumentData;
      expressionId?: string | null;
    }
  | {
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.PAYMENT_GATE;
      data: Record<string, never>;
      expressionId?: string | null;
    };

export { Roles };
export type { SlateNode, SlateElement };

export type SecondaryStripeAccount = {
  id: string;
  stripeAccountId: string;
  name: string;
  creatorId: string;
};

export type DestinationAccount = {
  id: string;
  provider: 'stripe' | 'confido' | 'quickbooks';
  providerId: string;
  name: string;
  creatorId: string;
  isFromDefault: boolean;
};

export type Document = {
  createdAt: string;
  creatorId: string;
  personId: string;
  isCollaborator: boolean;
  fileName: string;
  fileSize: number;
  id: string;
  mimeType: string;
  title: string | null; // @deprecated - attribute of UserWorkflowDocument, not Document.
  isEncrypted: boolean;
};

export type Organization = {
  id: string;
  name: string;
  ownerId: string;
  creatorId: string;
};

export type OrganizationMember = {
  id: string;
  personId: string;
  organizationId: string;
  canAssign: boolean;
  canInviteCollaborators: boolean;
  canReceiveCustomerNotifications: boolean;
  canInitiateWorkflows: boolean;
  canMakePayments: boolean;
  inviteToAllCases: boolean;
};

export type StripePaymentMethod = Pick<StripeModels.PaymentMethod, 'id' | 'type'> & {
  card: Pick<
    NonNullable<StripeModels.PaymentMethod['card']>,
    'brand' | 'last4' | 'exp_month' | 'exp_year' | 'funding'
  > | null;
  us_bank_account: Pick<NonNullable<StripeModels.PaymentMethod['us_bank_account']>, 'bank_name' | 'last4'> | null;
  verifyWithMicrodeposits: {
    arrivalDate: Scalars['DateTime'];
    verificationUrl: string;
    microdepositType: 'amounts' | 'descriptor_code' | 'unknown';
  } | null;
};

export type PaymentMethod = {
  provider: 'stripe' | 'confido';
  providerId: string;
  type: 'credit' | 'debit' | 'prepaid' | 'unknown' | 'ach';
  brand: string | null;
  bankName: string | null;
  last4: string | null;
};

export enum FollowupHistoryStatus {
  SENT = 'sent',
  SCHEDULED = 'scheduled',
  CANCELED = 'canceled',
}

export enum FollowupHistoryType {
  EMAIL = 'email',
  SMS = 'sms',
}

export type FollowupHistory = {
  id: string;
  messageAttachmentId: string;
  followupMessageText: string;
  messageId: string | null;
  status: FollowupHistoryStatus;
  type: FollowupHistoryType;
  scheduledAt: Scalars['DateTime'];
};

export type UserWorkflowDocumentIntegrations = 'pacer';

/**
 * File name types for PACER document integration.
 * Includes auto-generated core files, user-selectable document types, and legacy aliases.
 *
 * IMPORTANT: These string literals are contractual identifiers used across noodle-frontend,
 * noodle-documents, us-government-integrations, and district scripts. Do not modify without
 * coordinated updates across all repositories.
 */
export type UserWorkflowDocumentIntegrationFileNames =
  // Auto-generated core PACER files (generated from questionnaire data)
  | 'debtor.txt'
  | 'petition.pdf'
  | 'creditor.txt'
  | 'credit.pdf'
  // Legacy user-selectable aliases (deprecated - kept for backward compatibility with existing DB values)
  // TODO: Remove after migration is complete and all district scripts are updated
  | '121 - SSN Statement.pdf'
  | '2030 - Attorney Comp.pdf'
  | '122A1 - Means Test.pdf'
  | 'Service Agreement.pdf'
  | 'DSO.pdf'
  | 'Paystubs.pdf'
  // Current user-selectable document types
  | 'Other.pdf'
  | 'Credit Counseling Certificate (Debtor 1).pdf'
  | 'Credit Counseling Certificate (Debtor 2).pdf'
  | 'Statement of Social Security (Form 121).pdf'
  | 'Attorney Compensation Disclosure (Form 2030).pdf'
  | 'Current Monthly Income (CMI) (Form 122A-1).pdf'
  | 'Client or Professional Service Agreement.pdf'
  | 'Signature Pages.pdf'
  | 'Paystubs, Advices, Income Records (Debtor 1).pdf'
  | 'Paystubs, Advices, Income Records (Debtor 2).pdf'
  | 'PhotoID (Debtor 1).pdf'
  | 'PhotoID (Debtor 2).pdf'
  | 'DeBN (Debtor 1).pdf'
  | 'DeBN (Debtor 2).pdf'
  | 'Means Test Calculation (Form 122A-2).pdf'
  | 'Application for Individuals to Pay the Filing Fee in Installments (103A).pdf'
  | 'Application to Have the Chapter 7 Filing Fee Waived (103B).pdf'
  | 'Chapter 13 Plan.pdf'
  | 'Domestic Support Obligations (DSO).pdf'
  | 'Chapter 13 Statement of Your Current Monthly Income and Calculation of Commitment Period (122C-1).pdf'
  | 'Chapter 13 Calculation of Your Disposable Income (122C-2).pdf'
  // District-specific local forms
  | 'lf67.pdf';

export type UserWorkflowDocument = {
  id: string;
  rank: number;
  referenceId: string | null;
  referencePublishedAt: Scalars['DateTime'] | null;
  referenceType:
    | 'anvil'
    | 'direct-document'
    | 'document-request-file'
    | 'document-request-file-document'
    | 'mapping-destination'
    | 'manually-added'
    | 'ai-bot'
    | 'custom-terms'
    | 'questionnaire-pdf-fill'
    | 'questionnaire-generated'
    | 'case-document'
    | null;
  referenceVersion: string | null;
  templateReferenceId: string | null;
  templateReferenceType:
    | 'anvil'
    | 'direct-document'
    | 'document-request-file'
    | 'document-request-user-file'
    | 'manually-added'
    | 'ai-bot'
    | 'custom-terms-template'
    | 'mapping-destination'
    | null;
  title: string | null;
  userWorkflowId: string;
  generatedContent: { children: Descendant[] } | null;
  path: string | null;
  integrationSlug: UserWorkflowDocumentIntegrations | null;
  integrationFileName: UserWorkflowDocumentIntegrationFileNames | null;
};

export type TeamMember = {
  id: string;
  email: string | null;
  name: string | null;
  image: { url: string } | null;
  phoneNumber: string | null;
  primaryColour: { hex: string } | null;
  role: TeamRole;
  defaultWorkflowRole: string | null;
  teamMemberId: string;
  isOwner: boolean;
};

export type WorkflowRole = {
  id: string;
  name: string;
  rank: number;
  creatorId: string;
};

export type TaskTypedData =
  | {
      data: {
        productId: string | null;
      };
      action: 'generate-invoice';
    }
  | {
      data: {
        productId: string | null;
      };
      action: 'pay-invoice';
    }
  | {
      data: Record<string, never>;
      action: 'review-document-request';
    }
  | {
      data: Record<string, never>;
      action: 'upload-document-request';
    }
  | {
      data: Record<string, never>;
      action: 'agree-to-terms';
    }
  | {
      data: Record<string, never>;
      action: 'submit-document-request-for-review';
    }
  | {
      data: Record<string, never>;
      action: 'generate-terms';
    }
  | {
      data: Record<string, never>;
      action: 'complete-form-request';
    }
  | {
      data: Record<string, never>;
      action: 'review-form-request';
    }
  | {
      data: {
        customerName?: string | null;
        lastPurchasedPrice?: string | null;
        senderId?: string | null;
      };
      action: 'respond-to-conversation';
    }
  | {
      data: {
        senderId: string;
        threadId?: string;
        messageContext?:
          | {
              contextType: 'document-request';
              documentRequestUserId: string;
            }
          | {
              contextType: 'document-request-file';
              documentRequestUserFileId: string;
              documentRequestUserId?: string;
            };
      };
      action: 'respond-to-discussion';
    }
  | {
      data: {
        booking: Pick<Booking, 'productId' | 'id' | 'priceId' | 'startAt' | 'teamMemberPersonId'> & {
          price: Pick<Price, 'id' | 'priceTitle'> & {
            product: Pick<Product, 'id' | 'title'>;
          };
          person: Pick<Person, 'id' | 'name'> & {
            primaryColour?: { hex: string } | null;
            image?: { url: string } | null;
          };
          creator: Pick<Creator, 'id' | 'timezone'>;
        };
      };
      action: 'schedule-booking';
    }
  | {
      data: {
        booking: Pick<Booking, 'productId' | 'id' | 'priceId' | 'startAt' | 'teamMemberPersonId'> & {
          price: Pick<Price, 'id' | 'priceTitle'> & {
            product: Pick<Product, 'id' | 'title'>;
          };
          person: Pick<Person, 'id' | 'name'> & {
            primaryColour?: { hex: string } | null;
            image?: { url: string } | null;
          };
          creator: Pick<Creator, 'id' | 'timezone'>;
        };
      };
      action: 'select-booking-availability';
    }
  | {
      data: Record<string, never>;
      action: 'buy-credit-report';
    }
  | {
      data: Record<string, never>;
      action: 'approve-credit-report';
    }
  | {
      data: Record<string, never>;
      action: 'e-signature-request';
    }
  | {
      data: {
        workflowStepId: string;
        requiredRoleId: string;
        stepName: string;
        userWorkflowId: string;
        isBlockingNextSteps: boolean;
        blockedStepId?: string;
      };
      action: 'assign-workflow-step';
    };

export type Task = {
  id: string;
  title: string;
  createdAt: Scalars['DateTime'];
  completedAt: Scalars['DateTime'] | null;
  referenceType:
    | 'custom-terms'
    | 'document-request-user'
    | 'form-request'
    | 'invoice'
    | 'conversation'
    | 'booking'
    | 'credit-report'
    | 'e-signature-request'
    | 'workflow-step';
  referenceId: string;
  data: unknown;
  action: TaskTypedData['action'];
};

export type FullTaskResponse = Pick<Task, 'id' | 'title' | 'createdAt' | 'referenceId' | 'completedAt'> &
  TaskTypedData & {
    assignees: Array<Pick<Person, 'id' | 'name' | 'email'>>;
    totalPaid: number;
    userWorkflow:
      | (Pick<UserWorkflow, 'id'> & {
          person: Pick<Person, 'id' | 'name' | 'email'>;
          workflow: Pick<Workflow, 'id' | 'name'>;
          contexts: Array<Pick<UserWorkflowContext, 'id' | 'type' | 'label' | 'rank' | 'value'>>;
        })
      | null;
  };

export type WorkflowInvoice = {
  id: string;
  expressionId: string | null;
  workflowId: string;
  invoiceTemplateId: string;
  title: string | null;
  workflowInvoiceAssigneeIds: string[] | null;
};

export type ProcessingFeesResponse = {
  achFee: number;
  internationalCardFee: number;
  USCardFee: number;
};

export type AiFollowUpItem = {
  id: string;
  date: Date;
  text: string;
  title: string;
  type: FollowupHistoryType;
  person: {
    id: string;
    color: string | null;
    name: string | null;
    numActiveWorkflows: number;
    url: string | null;
  };
};

export type DailyBrief = {
  text: string;
  generatedAt: Date;
};

export type BuildingBlockDependencyCondition = {
  type: 'form-field-response';
};

export type ConditionVariable = {
  referenceType: 'form-field-variable';
};

export type ConfidoBankAccount = {
  id: string;
  category: 'operating' | 'trust';
  nickname: string;
  isDefault: boolean;
  lastFour: string | null;
};

export type TemplateMessage = {
  paragraphs: Array<{
    text: string | string[];
  }>;
  attachments: Array<{
    assignToSlugs?: string[];
    referenceSlug: string; // possible to type? would need to be keyof invoices, formRequests, etc depending on referenceType
    referenceType: MessageAttachment['referenceType'];
  }>;
};

export type WorkflowTemplate = {
  category: 'unknown' | 'immigration' | 'bankruptcy' | 'personal-injury';
  caseType: string | null;
  customTerms: Array<{
    isFollowupEnabled: boolean;
    newDynamicVariables: string[];
    slug: string;
    title: string;
    templateContent: string;
  }>;
  description: string;
  documentRequests: Array<
    Pick<DocumentApiModels.DocumentRequest, 'isFollowupEnabled' | 'title'> & {
      files: Array<
        {
          title: string;
          description: string;
          isRequired: boolean;
          isForYourEyesOnly: boolean;
        } & {
          group?: {
            slug: string;
            rank: number;
          };
        }
      >;
      slug: string;
    }
  >;
  formRequests: Array<
    Pick<
      FormRequestTemplate,
      | 'canAutoComplete'
      | 'followupCadence'
      | 'followupCadenceUnits'
      | 'inheritanceScheme'
      | 'isFollowupEnabled'
      | 'name'
      | 'provider'
      | 'providerLocator'
    > & {
      slug: string;
    }
  >;

  invoices: Array<
    Pick<
      InvoiceTemplate,
      | 'followupCadence'
      | 'followupCadenceUnits'
      | 'isAchPaymentEnabled'
      | 'isCreditCardPaymentEnabled'
      | 'isDebitCardPaymentEnabled'
      | 'isPartialPaymentEnabled'
      | 'areClientModificationDisabled'
      | 'modificationNotAllowedMessage'
      | 'isExemptFromApplicationFee'
      | 'isFollowupEnabled'
      | 'passProcessingFeesToCustomer'
      | 'title'
      | 'minimumAmount'
    > & {
      lineItems: Array<Pick<InvoiceTemplateLineItem, 'item' | 'price' | 'quantity'>>;
      slug: string;
    }
  >;
  product: {
    slug: string;
    title: string;
  };
  productType: {
    slug: string;
    title: string;
  };
  createdAt: Scalars['DateTime'];
  slug: string;
  title: string;
  version: number;
  workflows: Array<{
    compiledDocuments: Array<
      | { type: 'manually-added'; title: string }
      | { type: 'anvil-cast'; anvilName: string; noodleName?: string }
      | { type: 'document-request'; slug: string }
      | { type: 'document-request-file'; title: string; slug: string }
      | { type: 'document-request-group'; slug: string }
      | { type: 'direct-document'; id: string; title: string }
      | { type: 'ai-bot'; bot: string; title: string }
    >;
    context: Array<
      Pick<WorkflowContext, 'label' | 'type' | 'isTitleField'> & {
        variableName?: string | null;
        variables: Array<{
          type: string;
          variableName: string;
        }>;
        slug: string;
      }
    >;
    enableUscisUpdates?: boolean;
    hasGeneratedPdfs: boolean;
    invoices: string[]; // keyof invoices
    name: string;
    sendEmailToCreatorOnCompletion: boolean;
    threads: Array<{
      initiate:
        | {
            eventName: WORKFLOW_TRIGGERS.INITIATE_CTA;
            CTA: string;
            description: string;
          }
        | {
            eventName: WORKFLOW_TRIGGERS.USER_WORKFLOW_CTA;
            title: string;
          }
        | {
            eventName: WORKFLOW_TRIGGERS.INITIATE_THREAD;
            title: string;
            buildingBlockDependencies?: Array<{
              referenceSlug: string;
              referenceType: MessageAttachment['referenceType'];
              conditions?: Array<{
                type: BuildingBlockDependencyCondition['type'];
                expression: string;
                variables: Array<{
                  name: string;
                  referenceType: ConditionVariable['referenceType'];
                  referenceId: string;
                }>;
              }>;
            }>;
          }
        | {
            eventName: WORKFLOW_TRIGGERS.PURCHASE_INITIATED;
            title: string;
          }
        | {
            eventName: WORKFLOW_TRIGGERS.PRODUCT_PURCHASED;
            title: string;
          }
        | {
            eventName: WORKFLOW_TRIGGERS.BOOKING_SCHEDULED;
            title: string;
          };
      messages: Array<TemplateMessage | TemplateMessage[]>;
    }>;
  }>;
};

const CaseStatus = z.object({
  case_status: z.object({
    currentActionCode: z.string().optional(),
    currentActionCodeDate: z.nullable(z.string()).optional(),
    current_case_status_desc_en: z.string(),
    current_case_status_desc_es: z.string(),
    current_case_status_text_en: z.string(),
    current_case_status_text_es: z.string(),
    formType: z.string(),
    hist_case_status: z.nullable(
      z.array(
        z.object({
          action_code: z.string().optional(),
          completed_text_en: z.string(),
          completed_text_es: z.string(),
          date: z.string(),
          jurisdiction: z.nullable(z.string()).optional(),
          jurisdictionDescription: z.nullable(z.string()).optional(),
        }),
      ),
    ),
    jurisdiction: z.nullable(z.string()).optional(),
    jurisdictionDescription: z.nullable(z.string()).optional(),
    modifiedDate: z.nullable(z.string()),
    receiptNumber: z.string(),
    submittedDate: z.nullable(z.string()),
  }),
  message: z.string(),
});

export type UscisCaseStatus = z.infer<typeof CaseStatus>;

export type PaymentPlan = {
  id: string;
  invoiceId: string;
  // @deprecated - use paymentMethod.provider + .providerId instead.
  paymentMethodId: string;
  status: 'active' | 'completed' | 'failed' | 'canceled';
  frequency: 'monthly' | 'weekly' | 'biweekly' | 'bimonthly';
  amount: number;
  startDate: Scalars['DateTime'];
  nextPaymentDate: Scalars['DateTime'] | null;
  canceledAt: Scalars['DateTime'] | null;
  createdAt: Scalars['DateTime'];
  paymentPlanPayments: {
    id: string;
    expectedAmount: number;
    paymentPlanId: string;
    expectedDate: Scalars['DateTime'];
    status: 'upcoming' | 'paid' | 'failed' | 'retrying' | 'pending' | 'deferred';
  }[];
  paymentMethod: PaymentMethod | null;
};

export type PaymentPlanPayment = {
  expectedDate: Scalars['DateTime'];
  id: string;
  expectedAmount: number;
  paymentPlanId: string;
  paymentPlan: PaymentPlan;
  status: 'upcoming' | 'paid' | 'failed' | 'retrying' | 'pending' | 'deferred';
};

export type WorkflowOwner = {
  personId: string;
  workflowRoles?: string[];
  workflowId: string;
};

export type ExpressionVariable = {
  id: string;
  variableName: string;
  selector: string;
  expressionId: string;
  referenceId: string;
  referenceType:
    | 'case-data'
    | 'credit-report'
    | 'data-reference-slug'
    | 'field-template-key'
    | 'noodle-questionnaire'
    | 'workflow-attachment'
    | 'workflow-context'
    | 'field-template-list-id-map'
    | 'document-request-internal-data';
};

export type Expression = {
  id: string;
  expression: string;
  filterExpression: string | null;
  filterExpressionVariableName: string | null;
  filterExpressionOperand: string | null;
  filterExpressionItemSelector: string | null;
  filterExpressionReducer: 'sum' | null;
  variables: Array<Pick<ExpressionVariable, 'variableName' | 'selector' | 'referenceId' | 'referenceType'>>;
};

export type CommonWorkflowResponse = Pick<
  Workflow,
  | 'caseType'
  | 'creatorId'
  | 'estimatedTotalFees'
  | 'hasGeneratedPdfs'
  | 'id'
  | 'isEnabled'
  | 'isLocked'
  | 'name'
  | 'noodleFee'
  | 'referenceId'
  | 'referenceType'
  | 'canInitiateAssociatedWorkflows'
  | 'preserveWorkflowAssignment'
  | 'sendEmailToCreatorOnCompletion'
  | 'sendEmailToCreatorOnFirstPayment'
  | 'type'
  | 'version'
  | 'enableUscisUpdates'
  | 'enablePacerSubmissions'
  | 'slug'
> & {
  /** @deprecated use threads + stepsMap instead */
  steps: Array<
    Pick<WorkflowStep, 'id' | 'type' | 'eventName' | 'data' | 'title' | 'workflowStatus' | 'ownerAssignment'>
  >;
  stepsMap: Record<
    string /* id */,
    Pick<WorkflowStep, 'data' | 'eventName' | 'id' | 'title' | 'type' | 'workflowStatus' | 'ownerAssignment'>
  >;
  owners: Array<Pick<WorkflowOwner, 'workflowId' | 'personId' | 'workflowRoles'>>;
  contexts: Array<
    Pick<
      WorkflowContext,
      'id' | 'label' | 'type' | 'isTitleField' | 'isOptional' | 'enableInvoiceView' | 'options' | 'slug'
    > & {
      expression?: Expression | null;
      variables: Pick<WorkflowContextVariable, 'id' | 'variableName'>[];
    }
  >;
  workflowInvoices: Array<
    Pick<WorkflowInvoice, 'id' | 'title' | 'invoiceTemplateId' | 'workflowInvoiceAssigneeIds'> & {
      expression?: Expression | null;
      invoiceTemplate: Pick<
        InvoiceTemplate,
        | 'autoAssignCreatorInWorkflows'
        | 'autoAssignCustomerInWorkflows'
        | 'creatorId'
        | 'id'
        | 'isAchPaymentEnabled'
        | 'isCreditCardPaymentEnabled'
        | 'isDebitCardPaymentEnabled'
        | 'isFollowupEnabled'
        | 'isPartialPaymentEnabled'
        | 'areClientModificationDisabled'
        | 'modificationNotAllowedMessage'
        | 'passProcessingFeesToCustomer'
        | 'priceId'
        | 'secondaryStripeAccountId'
        | 'title'
        | 'confidoBankAccountId'
        | 'isExemptFromApplicationFee'
        | 'minimumAmount'
        | 'maxPaymentPlanMonths'
      > & {
        lineItems: Array<
          Pick<
            InvoiceTemplateLineItem,
            'id' | 'item' | 'price' | 'quantity' | 'description' | 'customTermsVariableName'
          >
        >;
      };
    }
  >;
  buildingBlocks: {
    customTerms: Pick<CustomTermsTemplate, 'id' | 'title'>[];
    documentRequests: Pick<DocumentApiModels.DocumentRequest, 'id' | 'title'>[];
    formRequests: Pick<FormRequestTemplate, 'id' | 'name'>[];
    invoices: Pick<InvoiceTemplate, 'id' | 'title'>[];
    bookingPrices: Pick<GraphQLModels.Price, 'id' | 'priceTitle'>[];
  };
  threads: Array<{
    rank: number;
    stepIds: string[];
    workflowStatuses: (string | null)[];
  }>;
};

export type SimpleWorkflowResponse = Pick<
  Workflow,
  | 'creatorId'
  | 'id'
  | 'isEnabled'
  | 'isLocked'
  | 'name'
  | 'noodleFee'
  | 'referenceId'
  | 'referenceType'
  | 'type'
  | 'version'
  | 'slug'
> & {
  steps: Array<Pick<WorkflowStep, 'id' | 'type' | 'eventName' | 'data' | 'title'>>;
  contexts: (Pick<WorkflowContext, 'id' | 'label' | 'type' | 'isTitleField' | 'isOptional' | 'slug' | 'options'> & {
    expression?: Expression | null;
    variables: Pick<WorkflowContextVariable, 'id' | 'variableName'>[];
  })[];
  workflowInvoices: Array<
    Pick<WorkflowInvoice, 'id' | 'title' | 'invoiceTemplateId'> & {
      expression?: Expression | null;
      invoiceTemplate: Pick<
        InvoiceTemplate,
        | 'autoAssignCreatorInWorkflows'
        | 'autoAssignCustomerInWorkflows'
        | 'areClientModificationDisabled'
        | 'modificationNotAllowedMessage'
        | 'creatorId'
        | 'id'
        | 'isAchPaymentEnabled'
        | 'isCreditCardPaymentEnabled'
        | 'isDebitCardPaymentEnabled'
        | 'isDebitCardPaymentRequiredForCustomer'
        | 'isFollowupEnabled'
        | 'isPartialPaymentEnabled'
        | 'passProcessingFeesToCustomer'
        | 'priceId'
        | 'secondaryStripeAccountId'
        | 'title'
        | 'confidoBankAccountId'
        | 'isExemptFromApplicationFee'
        | 'minimumAmount'
        | 'maxPaymentPlanMonths'
        | 'emailNotificationsEnabled'
      > & {
        lineItems: Array<
          Pick<
            InvoiceTemplateLineItem,
            'id' | 'item' | 'price' | 'quantity' | 'description' | 'customTermsVariableName'
          >
        >;
      };
    }
  >;
};

export type PersonProduct = {
  id: string;
  createdAt: Scalars['DateTime'];
  priceId: string;
  productId: string;
  creatorId: string;
  personId: string;
  customerId: string;
  confidoPaymentId: string | null;
  paymentIntentId: string | null;
  localCurrency: string | null;
  conversationId: string | null;
  customTermsId: string | null;
  discountCodeId: string | null;
  externalLicenseId: string | null;
  externalPurchaseId: string | null;
  hasViewedConfirmationPage: boolean;
  isFulfilled: boolean;
  sessionDuration: number | null;
  sessionTime: string | null; // Date + time without timezone. so leave as a string
};

export type PersonSubscription = {
  id: string;
  createdAt: Scalars['DateTime'];
  canceledAt?: Scalars['DateTime'] | null;
  lastBillingDate?: Scalars['DateTime'] | null;
  subscriptionStatus?: SubscriptionStatus | null;
  isFulfilled: boolean;
  stripeOrderId?: string | null;
  discountedPrice: number;
};

export type UserWorkflowOwner = {
  personId: string;
  workflowRoles?: string[];
  userWorkflowId: string;
};

export type UserWorkflowTag = {
  tagIcon: string;
  tagText: string;
};

export enum ACTIVITY_TYPES_AND_ACTIONS_ROLLUP {
  COMMENT_LEFT = 'comment-left',
  DOCUMENT_UPLOADED = 'document-uploaded',
  DOCUMENT_MOVED = 'document-moved',
  DOCUMENT_REQUEST_COMPLETED = 'document-request-completed',
  DOCUMENT_REQUEST_SUBMITTED_FOR_REVIEW = 'document-request-submitted-for-review',
  TERMS_AGREED = 'terms-agreed',
  USER_WORKFLOW_INITIATED = 'user-workflow-initiated',
  USER_WORKFLOW_STATUS_CHANGED = 'user-workflow-status-changed',
  FORM_REQUEST_COMPLETED = 'form-request-completed',
  QUESTIONNAIRE_DATA_IMPORT = 'questionnaire-data-import',
  FORM_REQUEST_INITIATED = 'form-request-initiated',
  PAYMENT_SUCCEEDED = 'payment-succeeded',
  PAYMENT_INITIATED = 'payment-initiated',
  PAYMENT_FAILED = 'payment-failed',
  CASE_NOTE_CREATED = 'case-note-created',
  CREDIT_REPORT_REQUEST = 'credit-report-request',
  CREDIT_REPORT_APPROVAL = 'credit-report-approval',
  CREDIT_REPORT_PULL = 'credit-report-pull',
  WORKFLOW_OWNER_ASSIGNED = 'workflow-owner-assigned',
  WORKFLOW_OWNER_UNASSIGNED = 'workflow-owner-unassigned',
  E_SIGNATURE_REQUEST_CREATED = 'e-signature-request-created',
  E_SIGNATURE_REQUEST_COMPLETED = 'e-signature-request-completed',
  COURT_NOTICE = 'court-notice',
  MEANS_TEST_AGENT_RUN = 'means-test-agent-run',
  EXEMPTIONS_AGENT_RUN = 'exemptions-agent-run',
  QUESTIONNAIRE_SUBMITTED_WITH_WARNINGS = 'questionnaire-submitted-with-warnings',
}

export enum ACTIVITY_TYPES {
  CREDIT_REPORT = 'credit-report',
  DOCUMENT_REQUEST = 'document-request',
  FORM_REQUEST = 'form-request',
  TERMS = 'terms',
  MESSAGE = 'message',
  STATUS_CHANGE = 'status-change',
  PAYMENT = 'payment',
  CASE_NOTE = 'case-note',
  OWNER_ASSIGNMENT = 'owner-assignment',
  COURT_NOTICE = 'court-notice',
  E_SIGNATURE_REQUEST = 'e-signature-request',
}

export type ActivityNoteAddedData = {
  text: string;
  metadata: {
    contactType?: string | null;
    date?: string | null;
    time?: string | null;
  } | null;
};

type USER_WORKFLOW_STATUS = string;

type ACTIVITY_ACTIONS_WITHOUT_STATUSES =
  | 'signed'
  | 'uploaded-document'
  | 'moved-document'
  | 'submitted-for-review'
  | 'completed'
  | 'initiated'
  | 'succeeded'
  | 'failed'
  | 'court-notice'
  | 'submitted-with-warnings';

export type ACTIVITY_ACTIONS = ACTIVITY_ACTIONS_WITHOUT_STATUSES | USER_WORKFLOW_STATUS | null;

export type UserWorkflowActivity = {
  id: Scalars['ID'];
  createdAt: Scalars['DateTime'];
  userWorkflowId: Scalars['ID'];
  personId: Scalars['ID'] | null;
  personName: Scalars['String'] | null;
  details: Scalars['Json'];
  activityType: ACTIVITY_TYPES_AND_ACTIONS_ROLLUP;
  action: ACTIVITY_ACTIONS;
  type: ACTIVITY_TYPES | null;
};

export type InboxItemType =
  | 'chat'
  | 'workflow-comment'
  | 'task'
  | 'noodle-broadcast'
  | 'payment-failed'
  | 'notification';

export type TypedInboxItemMeta =
  | {
      type: 'chat';
      meta: {
        conversationSid: string;
        message: string;
        messageId: string;
      };
    }
  | {
      type: 'workflow-comment';
      meta: {
        userWorkflowId: string;
        message: string;
        workflowName: string;
      };
    }
  | {
      type: 'payment-failed';
      meta: {
        userWorkflowId: string;
        message: string;
        workflowName: string;
      };
    }
  | {
      type: 'task';
      meta: unknown;
    }
  | {
      type: 'noodle-broadcast';
      meta: {
        message: string;
        messageId: string;
        title: string;
      };
    }
  | {
      type: 'notification';
      meta: {
        message: string;
        messageId: string;
        title: string;
        clientName: string;
        status: string;
        userWorkflowId: string;
      };
    };

export type UserInboxItem = {
  id: string;
  personId: string;
  senderId: string | null;
  taskId: string | null;
  type: TypedInboxItemMeta['type'];
  meta: unknown;
  isRead: boolean;
  createdAt: Date;
};

export type CommonUserInboxItem = Pick<UserInboxItem, 'id' | 'personId' | 'isRead' | 'createdAt'> &
  TypedInboxItemMeta & {
    sender: Pick<Person, 'name'> | null;
    task:
      | (Pick<Task, 'id' | 'title' | 'createdAt' | 'referenceId'> &
          TaskTypedData & {
            userWorkflow:
              | (Pick<UserWorkflow, 'id'> & {
                  person: Pick<Person, 'id' | 'name' | 'email'>;
                  workflow: Pick<Workflow, 'id' | 'name'>;
                })
              | null;
          })
      | null;
  };

export type businessCategory = {
  businessCategory: string[];
};

export type NoodleBroadcast = {
  ownerId: string | undefined;
  title: string;
  metadata: businessCategory;
  type: string;
  text: string[] | null;
};

export enum SENT_SMS_STATUS {
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
}

export enum SENT_SMS_TYPE {
  FOLLOWUP = 'followup',
  STANDARD = 'standard',
  CASE_INVITE = 'case-invite',
  OPT_IN = 'opt-in',
  CHAT_REMINDER = 'chat-reminder',
  BOOKING_REMINDER = 'booking-reminder',
  INCOMING_RESPONSE = 'incoming-response',
  INCOMING_MESSAGE = 'incoming-message',
}

export enum WORKFLOW_ATTACHMENT_STATUS {
  IN_PROGRESS = 'in-progress',
  COMPLETE = 'complete',
  SKIPPED = 'skipped',
  BLOCKED = 'blocked',
}

export type SentSmsMessage = {
  id: string;
  createdAt: Scalars['DateTime'];
  status: SENT_SMS_STATUS;
  type: SENT_SMS_TYPE;
  numSegments: number;
  messageBody: string;
  fromNumber: string;
  toNumber: string;
  billedInvoiceId: string | null;
  userWorkflowId: string | null;
  creatorId: string;
  personId: string;
};

export type CreditReportUserInfo = {
  city: string;
  dob: string;
  fname: string;
  lname: string;
  mname?: string;
  mobile: string;
  ssn: string;
  state: string;
  street1: string;
  street2?: string | number;
  zip: string;
};

export type UserVerificationResponse = {
  expires: string;
  mobile: string;
  token: string;
};

export type VerificationLinkResponse = {
  smsMessage: string;
  token: string;
  expires: string;
};

export type SmfaVerificationStatusResponse = {
  id: string;
  email: string;
  fname: string;
  lname: string;
  idpass: boolean;
  createdAt: string;
  updatedAt: string;
  smsMsg: boolean;
  emailMsg: boolean;
  pushMsg: boolean;
  flags: number;
  justEnrolled: boolean;
};

export type TeamRoleBooleans = 'finance' | 'manageTeam' | 'admin';

export type ClientMetric = {
  value: string | number | null;
  percentageChangeToLastMonth?: number;
  percentageChangeToLastYearThisMonth?: number;
  percentageChangeToLastYear?: number;
};

export type firmOverviewRecord = {
  timePeriod: string;
  totalNewClients: ClientMetric;
  totalRetainedClients: ClientMetric;
  sameDayClosedCount: ClientMetric;
  totalRevenueCollected: ClientMetric;
  avgRevenuePerClient: number;
  closingPercentage: number;
  sameDayClosedPercentage: number;
  monthWiseConversionCounts?: (number | null)[];
};

export type MetricsResponse = {
  yearlyMetrics: firmOverviewRecord[];
  monthlyMetrics: firmOverviewRecord[];
};

export type CurrentSalesReportRecord = {
  paymentVolume: number;
  transactionCount: number;
  averageTransactionAmount: number;
};

export type CurrentSalesOverViewResponse = {
  stats: {
    monthToDate: CurrentSalesReportRecord;
    yearToDate: CurrentSalesReportRecord;
  };
};

export type PaymentVolumeRecord = {
  date: string;
  totalAmount: number;
};

export type PaymentSourceRecord = {
  source: string;
  count: number;
};

export type RecentPayments = {
  amount: number;
  personName: string;
  personId: string;
  createdAt: string;
  id: string;
};

export type TopClients = {
  clientName: string;
  totalAmount: number;
  transactionCount: number;
};

export type SalesOverViewResponse = {
  recentPayments: RecentPayments[];
  topClients: TopClients[];
  stats: {
    paymentVolume: {
      value: number;
      percentageChange: number;
    };
    transactionCount: {
      value: number;
      percentageChange: number;
    };
    averageTransactionAmount: {
      value: number;
      percentageChange: number;
    };
  };
  paymentVolumeData: PaymentVolumeRecord[];
  paymentSourceData: PaymentSourceRecord[];
};

export type SaveSocialSecurityNumberRequest = {
  ssn: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
};

export type Company = {
  id: string;
  creatorId: string;
  name: string;
  pointOfContact?: string;
  email?: string;
  phoneNumber?: string;
  employees?: string[];
  color?: string;
};

export type Creditor = {
  id: string;
  creatorId: string;
  name: string;
  attention: string | null;
  address: USAddress;
};

export type Borrower = {
  firstName: string;
  middleName: string;
  lastName: string;
  birthDate: string;
  ssn: string;
  address: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
  };
};

export type AIRequest = {
  bot: string | null;
  botVersion: string | null;
  debug: unknown;
  error: string | null;
  finishedAt: Scalars['DateTime'] | null;
  generatedText: string | null;
  inputs: unknown;
  isAccepted: boolean;
  isFlagged: boolean;
  model: string | null;
  moderationData: unknown;
  request: unknown;
  response: unknown;
  tunings: unknown;
  type: string;
};

export type DistrictData = {
  code: string;
  name: string;
  filings: number;
};

export type StateData = {
  name: string;
  filings: number;
  districts: DistrictData[] | { [courtId: string]: DistrictData };
};

export type FilingData = {
  stateCode: string;
  filings: number;
};

export type DistrictDetail = {
  code: string;
  name: string;
  caseCount: number;
};

export type AttorneyCase = {
  name: string;
  caseCount: number;
  states: string[];
  primaryState: string;
  districts: string[];
  districtCount: number;
  districtDetails?: DistrictDetail[];
  primaryStateName?: string;
};

export type CSVRow = {
  courtId?: string;
  caseId?: string;
  dateFiled?: string;
  firstName?: string;
  lastName?: string;
  partyRole?: string;
  [key: string]: unknown;
};

export type StateOption = {
  code: string;
  name: string;
  caseCount: number;
};

export type DistrictOption = {
  code: string;
  name: string;
  stateCode: string;
};

export type AttorneyState = {
  name: string;
  caseCount: number;
  districts: Set<string>;
};

export type MapDataResponse = {
  stateData: { [stateCode: string]: StateData };
  filingStats: {
    maxFilings: number;
    minFilings: number;
    totalFilings: number;
    topStates: FilingData[];
  };
  lastUpdated: string;
};

export type BankruptcyDataResponse = {
  topAttorneys: AttorneyCase[];
  caDistricts: { district: string; caseCount: number }[];
  stateOptions: StateOption[];
  districtOptions: DistrictOption[];
  lastUpdated: string;
};
export type StateDetailResponse = {
  stateCode: string;
  stateName: string;
  totalFilings: number;
  districts: DistrictData[];
  topAttorneys: {
    name: string;
    caseCount: number;
    districtCount: number;
    districts: string[];
  }[];
  lastUpdated: string;
};

export type AttorneyDetailResponse = {
  name: string;
  caseCount: number;
  states: {
    code: string;
    name: string;
  }[];
  primaryState: {
    code: string;
    name: string;
  } | null;
  districts: DistrictData[];
  filingDates: {
    date: string;
    filings: number;
  }[];
  totalCases: number;
  lastUpdated: string;
};

export type TopAttorneyMonthly = {
  name: string;
  monthlyCaseCount: number;
  primaryState: string;
  primaryStateName: string;
};

export type LeaderboardMapStateData = {
  stateCode: string;
  stateName: string;
  totalCases: number;
  districts: {
    code: string;
    name: string;
    caseCount: number;
  }[];
};

export type MeansTestAiResponse = {
  calculationExplanation: string;
  calculationExplanationHtml: string;
  grossMonthlyIncome: number;
  alimonyAndMaintenancePayments: number;
  otherHouseholdIncome: number;
  interestDividendsAndRoyalties: number;
  unemploymentCompensation: number;
  pensionOrRetirementIncome: number;
  rentalPropertyIncome: number;
  businessProfessionIncome: number;
  /** Unix timestamp (milliseconds) for the end of the period month - use for reliable date operations. */
  periodEndMonthTimestamp?: number;
};

export type MeansTestResponse = {
  id: string;
  documentRequestUserId: string;
  userWorkflowId: string;
  aiRequestId: string | null;
  data: MeansTestAiResponse;
  status?: 'pending' | 'completed' | 'failed';
  aiFailedReason?: string | null;
};

export type CustomTermsVariable = {
  id: string;
  variableName: string;
  value: string | null;
  variableType: string | null;
  filledAt: Date | null;
  filledBy: string | null;
  signatoryName: string | null;
  assignedToPerson: string | null;
  assignedTo: string | null;
};

export type PlaidInstitution = {
  name: string;
  institution_id: string;
};

export type PlaidAccount = {
  id: string;
  name: string;
  mask: string;
  type: string;
  subtype: string;
  verification_status: string;
};

export type PlaidLinkOnSuccessMetadata = {
  institution: PlaidInstitution | null;
  accounts: Array<PlaidAccount>;
  link_session_id: string;
};

export type IntakeStatusReport = {
  client: {
    email?: string | null;
    id?: string;
    name?: string | null;
    phoneNumber?: string | null;
  } | null;
  createdDate: Date;
  initiatedBy?: string | null;
  lastActivity: Date;
  lastEmailed: Date | null;
  paralegal: Array<{
    id?: string;
    name?: string | null;
    primaryColor?: string | null;
  }>;
  intakeLead: Array<{
    id?: string;
    name?: string | null;
    primaryColor?: string | null;
  }>;
  recommendedCaseType: Array<{ id: string; name?: string }>;
  consultationDate: Date | null;
  workflow: {
    id: string;
    name?: string;
  } | null;
  workflowStatus: string | null;
};

export type ParalegalReport = {
  paralegalId: string;
  paralegal: string;
  casesInPreparation: number;
  casesFiled: number;
  droppedCases: number;
  pausedCases: number;
  archivedCases: number;
  filingRate: number;
  statuses: Array<{ slug: string; title: string; count: number }>;
};

export type DocumentsReport = {
  keyMetrics: {
    casesAssigned: number;
    incomingCases: number;
  };
  documentChecklistsReviewed: Array<{
    paralegalAndDocumentsName: string;
    count: number;
  }>;
  newWorkflowsAssigned: Array<{
    paralegalAndDocumentsName: string;
    count: number;
  }>;
  clientsServed: Array<{
    paralegalAndDocumentsName: string;
    percentage: number;
  }>;
};

export type CourtCalendarResponse = {
  creatorId: string;
  caseNumber: string | null;
  courtCode: string;
  client: string | null;
  trustee: string;
  attorney: string;
  hearingType: string;
  courtRoom: string;
  courtroomLocation: string | null;
  judge: string;
  details: string | null;
  eventTime: Date;
  generatedTime: Date;
  emailBody: string | null;
  zoomMeetingId: string | null;
  zoomPasscode: string | null;
  zoomPhone: string | null;
};

export type CourtCalendar = {
  [dateString: string]: CourtCalendarResponse[];
};

export type CourtCalendarFiltersResponse = {
  hearingType?: string[];
  attorney?: string[];
  judge?: string[];
  trustee?: string[];
  client?: string[];
  caseNumber?: string[];
  courtroomLocation?: string[];
  courtRoom?: string[];
};

export type InternalDataValue = unknown;
export type ExternalDataValue = string[];
export type QuestionnaireFieldValue = string[];
export type QuestionnaireListFieldValue = string[];

export type QuestionnaireFieldOutput = string[];
export type MeansTestOutput = string;

export type AgentType =
  | 'questionnaireField'
  | 'questionnaireBoxIndex'
  | 'questionnaireFilingDistrict'
  | 'questionnaireGooglePlaces'
  | 'questionnaireOmitForPdfFills'
  | 'meansTest';

export type AgentInput = {
  type: 'internalData' | 'externalData' | 'questionnaireField' | 'questionnaireListField';
  value: InternalDataValue | ExternalDataValue | QuestionnaireFieldValue | QuestionnaireListFieldValue;
};

export type AgentOutput = {
  type: 'questionnaireField' | 'meansTest';
  value: QuestionnaireFieldOutput | MeansTestOutput;
};

export type Agent = {
  id: string;
  creatorId: string;
  instructions: string | null;
  title: string;
  description: string;
  slug: string;
  isAutoRunEnabled: boolean;
  vellumWorkflowId: string | null;
  version: number;
  inputs: AgentInput[];
  outputs: AgentOutput[];
  type: AgentType;
  renderButtonReferenceId: string | null;
};

export type PacerEmailsWebhookEvent = {
  messageId: string;
  timestamp: string;
  rawEvent: {
    envelope: {
      mailFrom: { address: string };
      recipients: Array<{ address: string }>;
    };
    truncated: boolean;
    sender?: string | null;
    subject: string | null;
    messageId: string;
    invocationId?: string;
    flowDirection?: string;
  };
};

export type TaskEfficiencyResponseItem = {
  /** Timestamp of when the task was started/created */
  startedAt: Scalars['DateTime'];
  /** Timestamp of first completion, null if never completed */
  firstCompletedAt: Scalars['DateTime'] | null;
  /** Timestamp of most recent completion, null if never completed */
  lastCompletedAt: Scalars['DateTime'] | null;
  /** Number of times the task was reopened after completion */
  reopenCount: number;
};

export type TaskEfficiencyReportListItem = {
  id: string;
  createdAt: Scalars['DateTime'];
  status: 'pending' | 'completed' | 'failed';
  error: string | null;
  filterParameters: Record<string, unknown>;
};

export type TaskEfficiencyAggregateItem = {
  templateId: string | null;
  templateName: string | null;
  referenceType:
    | 'invoice'
    | 'custom_terms'
    | 'form_request'
    | 'document_request_user'
    | 'credit_report'
    | 'booking'
    | 'e_signature_request'
    | 'bank_account_integration';
  totalStarted: number;
  totalCompleted: number;
  /** Ratio of completed to started tasks (0–1, rounded to 4 decimal places). */
  completionRate: number;
  /** Average milliseconds from startedAt to firstCompletedAt (null if no completions). */
  avgTimeToCompleteMs: number | null;
  /** Median (p50) milliseconds from startedAt to firstCompletedAt (null if no completions). */
  medianTimeToCompleteMs: number | null;
  /** 90th-percentile milliseconds from startedAt to firstCompletedAt (null if no completions). */
  p90TimeToCompleteMs: number | null;
  /** Ratio of tasks reopened at least once to total completed tasks (0–1). */
  reopenRate: number;
  /** Average reopen count among tasks that were reopened at least once (excludes never-reopened tasks). */
  avgReopenCount: number;
};

export type TaskEfficiencyReportResponse = TaskEfficiencyReportListItem & {
  content: string | null;
};

export type CourtNoticesResponse = {
  messageId: string;
  creatorId: string;
  caseNumber: string;
  clientIntakeAdvocate: string;
  clientName: string | null;
  noticeLink?: string;
  noticeType: string | null;
  attachmentDocumentIds: string[];
  subject: string;
  time: Date | null;
  userWorkflowId?: string;
  userWorkflowName?: string | null;
  emailHTML?: string;
  userWorkflowCaseNumber?: string | null;
  documentDownloadUrl?: string | null;
};

export const ADDABLE_ATTACHMENT_TYPES = [
  MESSAGE_ATTACHMENT_REFERENCE_TYPES.CREDIT_REPORT,
  MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST_TEMPLATE,
  MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_TEMPLATE,
  MESSAGE_ATTACHMENT_REFERENCE_TYPES.BANK_ACCOUNT_INTEGRATION,
  MESSAGE_ATTACHMENT_REFERENCE_TYPES.E_SIGNATURE_REQUEST,
  MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS_TEMPLATE,
] as const;

export type AddableReferenceTypes = (typeof ADDABLE_ATTACHMENT_TYPES)[number];

// ---------------------------------------------------------------------------
// Case Data
// ---------------------------------------------------------------------------

export type CaseDataProvenance = {
  actorId: string | null;
  createdAt: string;
  sourceCategory: string;
  sourceId: string | null;
};

export type CaseDataFieldVersion = {
  id: string;
  provenance: CaseDataProvenance;
  value: unknown;
};

export type ResolvedCaseDataField = {
  fieldPath: string;
  history: CaseDataFieldVersion[];
  isConflicted: boolean;
  provenance: CaseDataProvenance;
  value: unknown;
};

export type CaseDataResponse = {
  caseId: string;
  entityCounts?: Record<string, number>;
  fields: Record<string, ResolvedCaseDataField | null>;
  hasConflicts: boolean;
};

export type CaseDataSchemaField = {
  fieldPath: string;
  type: 'date' | 'number' | 'string';
};

export type CaseDataEntitySchemaField = {
  fieldPath: string;
  type: string;
};

export type CaseDataEntityTypeSchema = {
  fields: CaseDataEntitySchemaField[];
};

export type CaseDataSchemaResponse = {
  fields: CaseDataSchemaField[];
  entities: Record<string, CaseDataEntityTypeSchema>;
  version: number;
};

export type ConflictSource = {
  sourceCategory: string;
  sourceId: string | null;
  value: unknown;
  createdAt: string;
};

export type ConflictDetail = {
  fieldPath: string;
  currentValue: unknown;
  sources: ConflictSource[];
  hasPendingReview: boolean;
};

export type CaseDataConflictsResponse = {
  caseId: string;
  conflicts: ConflictDetail[];
};

export type Resolution = {
  fieldPath: string;
  value: unknown;
  sourceCategory: string;
  notes?: string;
};

export type ResolveConflictsResponse = {
  caseId: string;
  resolvedCount: number;
};

export type CaseEntityField = {
  sourceCategory: string;
  value?: unknown;
};

export type CaseEntityListItem = {
  createdAt: string;
  entityType: string;
  fields: Record<string, CaseEntityField>;
  id: string;
  rank: number;
};
